import React, { useMemo, useRef, useEffect, useState } from 'react'
import {
  BiTrendingUp,
  BiTrendingDown,
  BiDollarCircle,
  BiPackage,
  BiCart,
  BiUser,
  BiCalendar,
  BiLineChart,
  BiBarChartAlt2,
  BiPieChartAlt2,
  BiTargetLock,
  BiLinkExternal,
  BiTable,
  BiChevronUp,
  BiChevronDown,
  BiShow,
  BiHide,
  BiCog,
  BiReset,
  BiWrench,
} from 'react-icons/bi'
import { formatCalendarDate } from '../../../modules/calendarDate'
import {
  FiTrendingUp,
  FiDollarSign,
  FiPackage,
  FiUsers,
  FiShoppingCart,
  FiTarget,
  FiAlertTriangle,
  FiCheckCircle,
  FiBarChart2,
} from 'react-icons/fi'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { detectAgentDataSource, getAgentDataSourceDisplayName } from '../utils/agentDataSource'

// Data source icons as inline SVGs for integrations
const DataSourceIcons: Record<string, React.FC<{ size?: number; className?: string }>> = {
  google: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  ),
  google_analytics: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden>
      <path fill="#F9AB00" d="M4 18h3v-7H4v7zm5 0h3V8H9v10zm5 0h3v-4h-3v4z" />
      <path fill="#E37400" d="M17 10h3v8h-3v-8z" />
    </svg>
  ),
  google_tag_manager: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden>
      <path fill="#246FDB" d="M4 5h7v7H4V5zm9 0h7v4h-7V5zm0 6h7v8h-7v-8zM4 14h7v5H4v-5z" />
      <path fill="#34A853" d="M6 16h3v1.5H6V16z" />
    </svg>
  ),
  klaviyo: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <path fill="#1A1A1A" d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18l7.5 3.75L12 11.68 4.5 7.93 12 4.18zM4 9.07l7 3.5v7.36l-7-3.5V9.07zm9 10.86v-7.36l7-3.5v7.36l-7 3.5z"/>
    </svg>
  ),
  shipsurance: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <path fill="#2563EB" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
    </svg>
  ),
  shopify: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <path fill="#96BF48" d="M15.337 3.415c-.03-.183-.148-.275-.275-.275-.122 0-2.474-.183-2.474-.183s-1.636-1.636-1.819-1.819c-.183-.183-.55-.122-.703-.092 0 0-.367.122-.947.306C8.783.555 8.447.098 7.835.037c-.916-.092-1.819.733-2.474 1.819-.519.855-1.053 1.941-1.191 2.765-.885.275-1.499.458-1.499.458-.458.122-.489.153-.55.58C2.06 6.054 0 23.5 0 23.5l12.474 2.283V3.843c-.03-.03-2.535-.458-2.535-.458s-.519 1.636-.672 3.89c-.489.153-.977.306-1.466.428 0-1.28-.183-3.462-.885-3.462-.703 0-1.28 1.758-1.636 3.432-.672.214-1.341.428-1.977.611 0 0 .336-2.657.306-2.932-.03-.275.092-.519.489-.611.275-.092 3.005-.916 3.005-.916s-.031-.214-.031-.55c-.031-.702-.55-1.282-1.099-1.312-.55-.031-1.007.367-1.343.764-.367.428-.764 1.129-.764 1.129l-2.443.733c.122-.764.458-1.941.977-2.796.977-1.605 2.322-1.697 2.932-1.007.61.703.427 1.988.427 2.443 0 .275.031.764.092 1.221 1.129-.336 2.535-.764 2.535-.764s.153-.855.336-1.819c.183-.916.519-2.046 1.465-2.046.55 0 .764.55.764 1.374 0 .977-.214 2.261-.214 3.157 0 0 2.657-.825 2.718-.855.214-.092.489-.061.55.183z"/>
    </svg>
  ),
  payhelm: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <circle cx="12" cy="12" r="10" fill="#3B82F6"/>
      <path fill="white" d="M8 8h4c1.1 0 2 .9 2 2v0c0 1.1-.9 2-2 2H8v4H6V8h2zm0 3h3.5c.28 0 .5-.22.5-.5v0c0-.28-.22-.5-.5-.5H8v1z"/>
      <path fill="white" d="M14 12h2v4h-2z"/>
    </svg>
  ),
  facebook: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  meta: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      <path fill="#0081FB" d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/>
    </svg>
  ),
  database: ({ size = 16, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <ellipse cx="12" cy="5" rx="9" ry="3"/>
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
    </svg>
  ),
}

interface AgentStep {
  iteration: number
  status: 'running' | 'completed' | 'failed'
  thought?: string
  tool?: string
  toolInput?: any
  toolOutput?: any
  timestamp?: number
}

interface AgentResultsDashboardProps {
  steps: AgentStep[]
  thoughts: string[]
  finalResponse?: string
  isExpanded: boolean
}

/**
 * Check if a tool is a configuration/account tool that returns metadata, not analytics data.
 * These tools should NOT be charted - they show account information, not data to visualize.
 */
const isConfigurationTool = (toolName?: string): boolean => {
  if (!toolName) return false
  const name = toolName.toLowerCase().replace('mcp__payhelm__', '')

  // Account listing tools - return list of connected accounts, not analytics
  const accountTools = [
    'get_google_ads_accounts',
    'list_google_ads_client_accounts',
    'get_facebook_ads_accounts',
    'get_openai_ads_accounts',
    'get_tiktok_accounts',
    'get_pinterest_accounts',
    'get_klaviyo_accounts',
    'get_bing_ads_accounts',
    'get_connected_accounts',
    'get_account_info',
    'get_account_details',
  ]

  // Settings/configuration tools - return settings info, not analytics
  const settingsTools = [
    'get_account_settings',
    'get_tracking_settings',
    'get_conversion_settings',
    'get_user_settings',
    'get_integration_status',
    'get_current_shipping_settings',
  ]

  return accountTools.includes(name) || settingsTools.includes(name)
}

// Aggregate data from all tool results
interface AggregatedData {
  revenue: {
    total: number
    trend: { date: string; value: number }[]
    byCategory: { name: string; value: number }[]
  }
  orders: {
    total: number
    trend: { date: string; value: number }[]
  }
  products: {
    topProducts: any[]
    totalProducts: number
  }
  customers: {
    total: number
    newCustomers: number
  }
  insights: string[]
  reportUrls: string[]
  rawResults: {
    query: string
    data: any[]
    reportUrl?: string
    isPeriodComparison?: boolean
    isConfigurationData?: boolean
    tool?: string
    toolInput?: any
    apiError?: string
    klaviyoErrors?: Array<{ detail?: string; title?: string; code?: string }>
  }[]
  periodComparisons: { query: string; data: PeriodComparison; reportUrl?: string }[]
}

// Detected chart data from auto-analysis
interface DetectedChart {
  type: 'line' | 'bar' | 'pie' | 'area'
  title: string
  categories: string[]
  values: number[]
  labelField: string
  valueField: string
}

// Detected metric from data
interface DetectedMetric {
  label: string
  value: number
  type: 'currency' | 'number' | 'percent'
}

// Period comparison data structure (like 2024 vs 2025)
interface PeriodComparison {
  metrics: {
    name: string
    values: { period: string; value: number }[]
    type: 'currency' | 'number' | 'percent'
  }[]
  periods: string[]
  periodStart?: string
  periodEnd?: string
}

// Year/period patterns for detection - more flexible
const YEAR_PATTERN = /^(20\d{2}|FY\d{2,4}|Q[1-4]\s*20\d{2}|H[12]\s*20\d{2})$/i
const YEAR_LIKE_PATTERN = /20\d{2}/ // Matches any string containing a year
const PERIOD_FIELD_PATTERN = /^(period|year|fiscal_year|fy|quarter|half)$/i

// Check if a key looks like a year column
const isYearColumn = (key: string): boolean => {
  // Exact match (e.g., "2024", "2025")
  if (YEAR_PATTERN.test(key)) return true
  // Contains year in various formats (e.g., "2024_value", "year_2024")
  if (YEAR_LIKE_PATTERN.test(key) && key.length < 15) return true
  return false
}

// Fields that typically contain dates
const DATE_FIELDS = ['date', 'day', 'month', 'period', 'created_at', 'updated_at', 'order_date', 'timestamp', 'time', 'year', 'week', 'date_trunc']

// Fields that typically contain category/label names (order matters - prefer name/title first)
const CATEGORY_FIELDS = [
  // Primary product identifiers (high priority)
  'title', 'name', 'product_title', 'product_name', 'item_name',
  'label', 'display_name',
  // Product/item fields
  'product', 'item', 'sku', 'catalog',
  // Organization/classification
  'category', 'type', 'brand', 'brand_name', 'vendor', 'vendor_name',
  // Customer
  'customer', 'customer_name',
  // Location
  'region', 'country', 'state', 'city',
  // Other
  'status', 'channel', 'source', 'description',
  'page', 'url', 'path'
]

// Fields that are IDs - should NOT be treated as values even if numeric
const ID_FIELDS = ['id', 'product_id', 'order_id', 'customer_id', 'user_id', 'variant_id', 'item_id', 'sku_id', 'category_id', 'shop_id', 'store_id']

// Fields that should NEVER be used as category labels (addresses, emails, etc.)
const EXCLUDED_CATEGORY_FIELDS = [
  'address', 'shipping_address', 'billing_address', 'full_address', 'street', 'street_address',
  'shipping_full_address', 'billing_full_address', 'address_line', 'address1', 'address2',
  'email', 'phone', 'zip', 'zipcode', 'postal', 'postal_code',
  'password', 'token', 'hash', 'secret', 'key', 'api_key',
  'note', 'notes', 'comment', 'comments', 'memo', 'internal_note',
  'json', 'metadata', 'attributes', 'properties', 'tags', 'raw',
  'ip', 'ip_address', 'user_agent', 'browser', 'device',
  'query', 'querystring', 'search_query'
]

// Check if a field is an ID field (should be excluded from value detection)
const isIdField = (field: string): boolean => {
  const lower = field.toLowerCase()
  return ID_FIELDS.some(idf => lower === idf || lower.endsWith('_id'))
}

// Check if a field should be excluded from category detection
const isExcludedCategoryField = (field: string): boolean => {
  const lower = field.toLowerCase()
  return EXCLUDED_CATEGORY_FIELDS.some(ef => lower === ef || lower.includes(ef))
}

// Fields that typically contain numeric values - includes sales, traffic, inventory fields
const VALUE_FIELDS = [
  // Sales/orders
  'total', 'revenue', 'amount', 'value', 'count', 'quantity', 'orders', 'sales', 'profit', 'cost', 'price',
  'total_revenue', 'total_sales', 'order_count', 'units', 'sum', 'avg', 'average', 'gross', 'net',
  'gross_sales', 'net_sales', 'items_sold', 'tax', 'shipping', 'discount', 'margin',
  // Traffic
  'pageviews', 'total_pageviews', 'sessions', 'visitors', 'unique_visitors', 'bounce_rate', 'duration',
  'carts', 'checkouts', 'total_carts', 'total_checkouts', 'total_orders', 'total_visitors', 'total_sessions',
  // Inventory
  'stock', 'inventory', 'available', 'on_hand', 'reserved',
  // MCP specific
  'line_item_total', 'subtotal', 'total_quantity', 'units_sold', 'qty',
  'customer_count', 'total_customers', 'unique_customers', 'num_orders',
  // Normalized alias fields (added via MCP_FIELD_MAPPINGS)
  'customers', 'new_customers', 'returning_customers', 'aov', 'conversion', 'margin', 'discounts', 'refunds',
  // Google Ads metrics
  'clicks', 'impressions', 'conversions', 'ctr', 'cpc', 'cost_micros', 'costmicros',
  // Facebook/Meta Ads metrics
  'spend', 'reach', 'frequency', 'cpm', 'cpp',
  // PayHelm specific - common report fields
  'sales_usd', 'total_price', 'total_items_sold', 'numfound', 'num_found',
  'unique_sessions', 'total_sessions', 'total_visitors',
  // Stats fields (from traffic/summary)
  'stats_pageviews', 'stats_sessions', 'stats_visitors', 'stats_total', 'stats_sales',
  // Year comparison
  'ytd', 'mtd', 'wtd', 'growth', 'change', 'percent', 'rate', 'ratio',
]

// Common MCP field name mappings - normalize to chart-friendly names
const MCP_FIELD_MAPPINGS: Record<string, { category?: string; value?: string }> = {
  // Product queries - these are common MCP field names for product data
  'product_title': { category: 'name' },
  'product_name': { category: 'name' },
  'item_name': { category: 'name' },
  'variant_title': { category: 'name' },
  'sku': { category: 'sku' },
  'product_sku': { category: 'sku' },
  // Brand/Category
  'brand_name': { category: 'brand' },
  'category_name': { category: 'category' },
  'product_type': { category: 'type' },
  // Customer queries
  'customer_name': { category: 'name' },
  'customer_email': { category: 'email' },
  'customer_group_name': { category: 'segment' },
  'first_name': { category: 'name' },
  'last_name': { category: 'name' },
  // Location queries
  'country_name': { category: 'country' },
  'state_name': { category: 'state' },
  'city_name': { category: 'city' },
  'region_name': { category: 'region' },
  // Revenue - these map MCP value fields to standard names
  'total_revenue': { value: 'revenue' },
  'gross_revenue': { value: 'revenue' },
  'net_revenue': { value: 'revenue' },
  'total_sales': { value: 'revenue' },
  'gross_sales': { value: 'revenue' },
  'net_sales': { value: 'revenue' },
  'line_item_total': { value: 'revenue' },
  'subtotal': { value: 'revenue' },
  'revenue_amount': { value: 'revenue' },
  'sales_amount': { value: 'revenue' },
  // Orders
  'order_count': { value: 'orders' },
  'total_orders': { value: 'orders' },
  'num_orders': { value: 'orders' },
  'orders_count': { value: 'orders' },
  // Quantity
  'total_quantity': { value: 'quantity' },
  'qty': { value: 'quantity' },
  'units_sold': { value: 'quantity' },
  'items_sold': { value: 'quantity' },
  'quantity_sold': { value: 'quantity' },
  // Customers
  'customer_count': { value: 'customers' },
  'total_customers': { value: 'customers' },
  'unique_customers': { value: 'customers' },
  'new_customers': { value: 'new_customers' },
  'returning_customers': { value: 'returning_customers' },
  // Other metrics
  'average_order_value': { value: 'aov' },
  'avg_order_value': { value: 'aov' },
  'aov': { value: 'aov' },
  'conversion_rate': { value: 'conversion' },
  'profit_margin': { value: 'margin' },
  'discount_amount': { value: 'discounts' },
  'refund_amount': { value: 'refunds' },
  // Google Ads fields
  'campaign_name': { category: 'name' },
  'ad_group_name': { category: 'name' },
  'clicks': { value: 'clicks' },
  'impressions': { value: 'impressions' },
  'conversions': { value: 'conversions' },
  'cost_micros': { value: 'cost' },
  'costMicros': { value: 'cost' },
  'ctr': { value: 'ctr' },
  'cpc': { value: 'cpc' },
  // Traffic fields
  'total_sessions': { value: 'sessions' },
  'totalSessions': { value: 'sessions' },
  'total_visitors': { value: 'visitors' },
  'totalVisitors': { value: 'visitors' },
  'total_pageviews': { value: 'pageviews' },
  'totalPageviews': { value: 'pageviews' },
  'total_carts': { value: 'carts' },
  'totalCarts': { value: 'carts' },
  'total_checkouts': { value: 'checkouts' },
  'totalCheckouts': { value: 'checkouts' },
  'bounce_rate': { value: 'bounce_rate' },
  // Inventory fields
  'stock_quantity': { value: 'stock' },
  'available_quantity': { value: 'available' },
  'on_hand_quantity': { value: 'on_hand' },
  'reserved_quantity': { value: 'reserved' },
}

// Normalize MCP data rows - add standard field aliases for better chart detection
const normalizeMcpData = (rows: any[]): any[] => {
  if (!rows || rows.length === 0) return rows

  return rows.map(row => {
    const normalized: Record<string, any> = { ...row }

    for (const [originalField, mapping] of Object.entries(MCP_FIELD_MAPPINGS)) {
      if (originalField in row) {
        // Add alias if it doesn't conflict with existing field
        if (mapping.category && !(mapping.category in normalized)) {
          normalized[mapping.category] = row[originalField]
        }
        if (mapping.value && !(mapping.value in normalized)) {
          normalized[mapping.value] = row[originalField]
        }
      }
    }

    // Also convert string numbers to actual numbers for value fields
    for (const key of Object.keys(normalized)) {
      const val = normalized[key]
      if (typeof val === 'string' && /^[\d.,]+$/.test(val.trim())) {
        const num = parseFloat(val.replace(/,/g, ''))
        if (!isNaN(num) && isValueField(key)) {
          normalized[key] = num
        }
      }
    }

    return normalized
  })
}


// Get priority category fields based on query context
// This helps select the right field for charts when multiple candidates exist
const getQueryBasedCategoryFields = (query: string): string[] => {
  if (!query) return []
  const lower = query.toLowerCase()

  // Product queries - prioritize product name fields
  if (lower.includes('product') || lower.includes('item') || lower.includes('sku') || lower.includes('selling')) {
    return ['name', 'product_title', 'product_name', 'title', 'item_name', 'sku', 'variant_title']
  }

  // Brand queries
  if (lower.includes('brand')) {
    return ['brand', 'brand_name']
  }

  // Category queries
  if (lower.includes('category') || lower.includes('type')) {
    return ['category', 'category_name', 'product_type', 'type']
  }

  // Customer queries
  if (lower.includes('customer') || lower.includes('segment') || lower.includes('group')) {
    return ['name', 'customer_name', 'customer_group_name', 'segment', 'email', 'customer_email']
  }

  // Location queries
  if (lower.includes('country') || lower.includes('region') || lower.includes('state') || lower.includes('city') || lower.includes('location')) {
    return ['country', 'country_name', 'region', 'region_name', 'state', 'state_name', 'city', 'city_name']
  }

  // Channel/source queries
  if (lower.includes('channel') || lower.includes('source') || lower.includes('utm')) {
    return ['channel', 'source', 'utm_source', 'traffic_source', 'marketing_channel']
  }

  return []
}

// Detect if a field name is a date field
const isDateField = (field: string): boolean => {
  const lower = field.toLowerCase()
  return DATE_FIELDS.some(df => lower.includes(df))
}

// Detect if a field name is a category field
const isCategoryField = (field: string): boolean => {
  const lower = field.toLowerCase()
  return CATEGORY_FIELDS.some(cf => lower === cf || lower.includes(cf))
}

// Detect if a field name is a value field
const isValueField = (field: string): boolean => {
  const lower = field.toLowerCase()
  return VALUE_FIELDS.some(vf => lower === vf || lower.includes(vf))
}

// Check if a value looks like a valid date (not just any string)
const isValidDateValue = (value: any): boolean => {
  if (!value) return false
  if (typeof value === 'number') return false // Numbers aren't dates for display

  const str = String(value)

  // Skip if it looks like an address, name, or other non-date string
  if (str.length > 50) return false
  if (str.includes('@')) return false // email
  if (/\d{5}/.test(str) && str.length < 15) return false // zipcode

  // Check for common date patterns
  const datePatterns = [
    /^\d{4}-\d{2}-\d{2}/, // 2024-01-15 or 2024-01-15T00:00:00
    /^\d{1,2}\/\d{1,2}\/\d{2,4}/, // 1/15/2024 or 01/15/24
    /^\d{1,2}-\d{1,2}-\d{2,4}/, // 1-15-2024
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i, // Jan 15, 2024
    /^\d{4}$/, // Year only: 2024
    /^\d{4}-\d{2}$/, // Year-month: 2024-01
  ]

  return datePatterns.some(p => p.test(str))
}

// Safely format a date value for chart display
const formatDateForChart = (value: any): string => {
  if (!value) return ''

  const str = String(value)

  // If it doesn't look like a date, return as-is
  if (!isValidDateValue(value)) {
    return str.length > 15 ? str.slice(0, 12) + '...' : str
  }

  const formatted = formatCalendarDate(str, { month: 'short', day: 'numeric' })
  if (!formatted || formatted === str) {
    // Couldn't parse as a nicer date — return original (truncated if needed)
    if (isNaN(new Date(str).getTime()) && !/^\d{4}-\d{2}-\d{2}/.test(str)) {
      return str.length > 15 ? str.slice(0, 12) + '...' : str
    }
  }
  return formatted || (str.length > 15 ? str.slice(0, 12) + '...' : str)
}

// Analyze data rows to detect structure
const analyzeDataStructure = (rows: any[], query?: string): { dateField?: string; categoryField?: string; valueFields: string[] } => {
  if (!rows || rows.length === 0) return { valueFields: [] }

  const firstRow = rows[0]
  const keys = Object.keys(firstRow)

  let dateField: string | undefined
  let categoryField: string | undefined
  const valueFields: string[] = []

  // Zero pass: check for query-based priority fields first
  // This helps select the right field when query mentions specific data types
  if (query) {
    const queryPriorityFields = getQueryBasedCategoryFields(query)
    if (queryPriorityFields.length > 0) {
      for (const priorityField of queryPriorityFields) {
        // Check if any key matches the priority field (case-insensitive)
        const matchingKey = keys.find(k => {
          const lower = k.toLowerCase()
          return lower === priorityField || lower.includes(priorityField)
        })
        if (matchingKey && typeof firstRow[matchingKey] === 'string' && !isExcludedCategoryField(matchingKey)) {
          categoryField = matchingKey
          break
        }
      }
    }
  }

  // First pass: if no query match, look for recognized category fields (high priority names like 'title', 'name')
  // This ensures we find product names even if date fields exist
  // Also check for customer_group_name which is a common grouping field
  if (!categoryField) {
    const priorityCategoryFields = ['title', 'name', 'product_title', 'product_name', 'product', 'item', 'brand', 'brand_name', 'customer_group_name', 'segment', 'customer_group', 'group_name']
    for (const key of keys) {
      const lower = key.toLowerCase()
      const sampleValue = firstRow[key]
      // Skip excluded fields even if they match priority names
      if (isExcludedCategoryField(key)) continue
      if (typeof sampleValue === 'string' && priorityCategoryFields.some(cf => lower === cf || lower.includes(cf))) {
        categoryField = key
        break
      }
    }
  }

  // Second pass: find date field and remaining category/value fields
  for (const key of keys) {
    const sampleValue = firstRow[key]

    // Check for date field - field name must look like a date AND the value must be a valid date
    if (!dateField && isDateField(key) && isValidDateValue(sampleValue)) {
      dateField = key
      continue
    }

    // Check for category field (strings that aren't dates) - if not already found
    // Skip excluded fields (addresses, emails, etc.)
    if (!categoryField && isCategoryField(key) && typeof sampleValue === 'string' && !isExcludedCategoryField(key)) {
      categoryField = key
      continue
    }

    // Check for value fields (numeric) - but exclude ID fields
    if (typeof sampleValue === 'number' || (typeof sampleValue === 'string' && !isNaN(parseFloat(sampleValue)))) {
      if (!isIdField(key) && isValueField(key)) {
        valueFields.push(key)
      }
    }
  }

  // Third pass: if no value fields found from recognized names, look for any numeric field
  if (valueFields.length === 0) {
    for (const key of keys) {
      const sampleValue = firstRow[key]
      const isNumeric = typeof sampleValue === 'number' || (typeof sampleValue === 'string' && !isNaN(parseFloat(sampleValue)))
      if (!isIdField(key) && !isDateField(key) && isNumeric) {
        valueFields.push(key)
      }
    }
  }

  // Fourth pass: DESPERATE FALLBACK - if still no value fields, try numeric ID-like fields
  // that have large values (not small integers that are likely actual IDs)
  if (valueFields.length === 0) {
    for (const key of keys) {
      const sampleValue = firstRow[key]
      const numValue = typeof sampleValue === 'number' ? sampleValue : parseFloat(sampleValue)
      if (!isNaN(numValue) && !isDateField(key)) {
        // Include if value is large (likely a metric, not an ID) or has decimals
        const hasDecimals = String(numValue).includes('.')
        const isLikelyMetric = Math.abs(numValue) > 100 || hasDecimals
        if (isLikelyMetric) {
          valueFields.push(key)
        }
      }
    }
  }

  // Fifth pass: LAST RESORT - just use the first numeric field we find
  if (valueFields.length === 0) {
    for (const key of keys) {
      const sampleValue = firstRow[key]
      const numValue = typeof sampleValue === 'number' ? sampleValue : parseFloat(sampleValue)
      if (!isNaN(numValue) && key !== '_title') {
        valueFields.push(key)
        break // Just take the first one
      }
    }
  }

  // If still no category field found, look for any string field that isn't excluded
  if (!categoryField) {
    for (const key of keys) {
      if (typeof firstRow[key] === 'string' && !isDateField(key) && !isIdField(key) && !isExcludedCategoryField(key)) {
        categoryField = key
        break
      }
    }
  }

  return { dateField, categoryField, valueFields }
}

// Detect if query suggests categorical/ranking data (bar chart)
const isCategoricalQuery = (query: string): boolean => {
  if (!query) return false
  const lower = query.toLowerCase()
  // Keywords that suggest ranking/comparison by category
  const categoryKeywords = [
    'top', 'best', 'worst', 'most', 'least', 'highest', 'lowest',
    'by product', 'by category', 'by brand', 'by customer', 'by region',
    'products by', 'categories by', 'brands by', 'customers by',
    'selling', 'performing', 'ranked', 'comparison'
  ]
  return categoryKeywords.some(kw => lower.includes(kw))
}

// Detect if query suggests time series data (line chart)
const isTimeSeriesQuery = (query: string): boolean => {
  if (!query) return false
  const lower = query.toLowerCase()
  // Keywords that suggest time-based analysis
  const timeKeywords = [
    'over time', 'trend', 'daily', 'weekly', 'monthly', 'yearly',
    'last 30 days', 'last 90 days', 'this month', 'this year',
    'compared to', 'vs previous', 'growth', 'change over'
  ]
  return timeKeywords.some(kw => lower.includes(kw))
}

// Format field name for display (revenue_total -> Revenue Total)
const formatFieldName = (field: string): string => {
  return field
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Generate a smart chart title based on the query intent
const generateSmartTitle = (query: string, valueField: string, categoryField?: string, isTimeSeries?: boolean): string => {
  if (!query) {
    // Fallback to field-based title
    if (isTimeSeries) return `${formatFieldName(valueField)} Over Time`
    if (categoryField) return `${formatFieldName(valueField)} by ${formatFieldName(categoryField)}`
    return formatFieldName(valueField)
  }

  const lower = query.toLowerCase()

  // Extract key concepts from the query
  const concepts = {
    // What metric are we looking at?
    metric: '',
    // What dimension/grouping?
    dimension: '',
    // Time context?
    timeContext: ''
  }

  // Detect metric from query
  if (lower.includes('revenue')) concepts.metric = 'Revenue'
  else if (lower.includes('sales')) concepts.metric = 'Sales'
  else if (lower.includes('orders') || lower.includes('order count')) concepts.metric = 'Orders'
  else if (lower.includes('customers') || lower.includes('customer count')) concepts.metric = 'Customers'
  else if (lower.includes('pageviews') || lower.includes('traffic')) concepts.metric = 'Pageviews'
  else if (lower.includes('conversion')) concepts.metric = 'Conversion Rate'
  else if (lower.includes('lifetime value') || lower.includes('ltv') || lower.includes('clv')) concepts.metric = 'Customer Lifetime Value'
  else if (lower.includes('growth')) concepts.metric = 'Growth'
  else if (lower.includes('count')) concepts.metric = 'Count'
  else concepts.metric = formatFieldName(valueField)

  // Detect dimension/grouping from query
  if (lower.includes('by segment') || lower.includes('segment')) concepts.dimension = 'by Segment'
  else if (lower.includes('by product') || lower.includes('products')) concepts.dimension = 'by Product'
  else if (lower.includes('by category') || lower.includes('categories')) concepts.dimension = 'by Category'
  else if (lower.includes('by customer') || lower.includes('customer group')) concepts.dimension = 'by Customer Group'
  else if (lower.includes('by channel') || lower.includes('channels')) concepts.dimension = 'by Channel'
  else if (lower.includes('by region') || lower.includes('regions')) concepts.dimension = 'by Region'
  else if (lower.includes('by brand') || lower.includes('brands')) concepts.dimension = 'by Brand'
  else if (lower.includes('top ')) {
    // Extract "top X" pattern
    const topMatch = lower.match(/top\s+(\d+|\w+)?\s*(products?|customers?|items?|categories?|brands?)?/i)
    if (topMatch) {
      const entity = topMatch[2] || 'Items'
      concepts.dimension = `Top ${entity.charAt(0).toUpperCase() + entity.slice(1)}`
    }
  }

  // Detect time context
  if (lower.includes('last 30 days') || lower.includes('last month')) concepts.timeContext = 'Last 30 Days'
  else if (lower.includes('last 90 days') || lower.includes('last quarter') || lower.includes('last 3 months')) concepts.timeContext = 'Last 90 Days'
  else if (lower.includes('last 6 months')) concepts.timeContext = 'Last 6 Months'
  else if (lower.includes('this month')) concepts.timeContext = 'This Month'
  else if (lower.includes('this year') || lower.includes('ytd')) concepts.timeContext = 'YTD'
  else if (lower.includes('year over year') || lower.includes('yoy')) concepts.timeContext = 'Year over Year'
  else if (lower.includes('over time') || lower.includes('trend')) concepts.timeContext = 'Over Time'

  // Build the title
  let title = concepts.metric

  if (isTimeSeries && concepts.timeContext) {
    title += ` ${concepts.timeContext}`
  } else if (concepts.dimension) {
    title += ` ${concepts.dimension}`
  } else if (isTimeSeries) {
    title += ' Over Time'
  } else if (categoryField && categoryField !== 'shipping_full_address') {
    title += ` by ${formatFieldName(categoryField)}`
  }

  // Add time context as suffix if we have a dimension
  if (concepts.dimension && concepts.timeContext && !isTimeSeries) {
    title += ` - ${concepts.timeContext}`
  }

  return title
}

// Auto-detect best chart type for data
// IMPORTANT: Skip single-row data - charts are meaningless with only 1 data point
const detectChartType = (rows: any[], structure: { dateField?: string; categoryField?: string; valueFields: string[] }, query?: string): DetectedChart | null => {
  // Minimum 2 rows required for any meaningful chart
  if (!rows || rows.length < 2 || structure.valueFields.length === 0) {
    return null
  }

  const { dateField, categoryField, valueFields } = structure
  const valueField = valueFields[0] // Use first value field
  const formattedValueField = formatFieldName(valueField)

  // Analyze query intent
  const querySuggestsCategorical = isCategoricalQuery(query || '')
  const querySuggestsTimeSeries = isTimeSeriesQuery(query || '')

  // Decision logic:
  // 1. If query clearly suggests categorical (top products, etc.) -> bar chart
  // 2. If query clearly suggests time series AND we have date field -> line chart
  // 3. If we have category field AND no strong time indicator -> bar chart
  // 4. If we have date field AND no category -> line chart
  // 5. Default to bar chart for categorical data

  // Priority 1: Query explicitly suggests categorical data (top X, best Y)
  if (querySuggestsCategorical && categoryField) {
    const categories = rows.slice(0, 10).map(r => String(r[categoryField]))
    const values = rows.slice(0, 10).map(r => parseFloat(r[valueField]) || 0)

    return {
      type: 'bar',
      title: generateSmartTitle(query || '', valueField, categoryField, false),
      categories,
      values,
      labelField: categoryField,
      valueField,
    }
  }

  // Priority 2: Query suggests time series AND we have date field
  if (querySuggestsTimeSeries && dateField) {
    const categories = rows.map(r => String(r[dateField]))
    const values = rows.map(r => parseFloat(r[valueField]) || 0)
    return {
      type: rows.length > 10 ? 'area' : 'line',
      title: generateSmartTitle(query || '', valueField, undefined, true),
      categories,
      values,
      labelField: dateField,
      valueField,
    }
  }

  // Priority 3: We have a category field (product name, brand, etc.) - prefer bar chart
  // This is important for "top products" type queries even without explicit keywords
  if (categoryField) {
    const categories = rows.slice(0, 10).map(r => String(r[categoryField]))
    const values = rows.slice(0, 10).map(r => parseFloat(r[valueField]) || 0)

    // Use pie for small datasets (<=6), bar for larger
    const chartType = rows.length <= 6 ? 'pie' : 'bar'

    return {
      type: chartType,
      title: generateSmartTitle(query || '', valueField, categoryField, false),
      categories,
      values,
      labelField: categoryField,
      valueField,
    }
  }

  // Priority 4: Only date field available - use time series
  if (dateField) {
    const categories = rows.map(r => String(r[dateField]))
    const values = rows.map(r => parseFloat(r[valueField]) || 0)
    return {
      type: rows.length > 10 ? 'area' : 'line',
      title: generateSmartTitle(query || '', valueField, undefined, true),
      categories,
      values,
      labelField: dateField,
      valueField,
    }
  }

  return null
}

// Detect if data is a period comparison (e.g., 2024 vs 2025)
const detectPeriodComparison = (rows: any[]): PeriodComparison | null => {
  if (!rows || rows.length === 0) return null

  const firstRow = rows[0]
  if (!firstRow || typeof firstRow !== 'object') return null

  const keys = Object.keys(firstRow)

  // Strategy 1: Look for columns that are year values (e.g., "2024", "2025")
  const yearColumns = keys.filter(k => isYearColumn(k))

  if (yearColumns.length >= 2) {
    // Found period columns - each row is a metric
    const metricNameField = keys.find(k => {
      const lower = k.toLowerCase()
      return lower === 'metric' || lower === 'name' || lower === 'description' ||
             lower === 'kpi' || lower === 'label' || lower === 'item'
    }) || keys.find(k => typeof firstRow[k] === 'string' && !isYearColumn(k))


    if (metricNameField) {
      const metrics = rows.map(row => {
        const name = String(row[metricNameField] || '')
        const values = yearColumns.map(period => ({
          period: period.replace(/[^0-9]/g, ''), // Extract just the year number
          value: parseFloat(row[period]) || 0
        }))
        // Detect value type
        const lowerName = name.toLowerCase()
        const isCurrency = lowerName.includes('revenue') || lowerName.includes('sales') ||
                          lowerName.includes('cost') || lowerName.includes('profit') ||
                          lowerName.includes('amount') || lowerName.includes('price') ||
                          lowerName.includes('gross') || lowerName.includes('net') ||
                          lowerName.includes('shipping') || lowerName.includes('refund') ||
                          lowerName.includes('order') // Orders often have dollar values
        const isPercent = lowerName.includes('margin') || lowerName.includes('rate') ||
                         lowerName.includes('percent') || lowerName.includes('%')
        return {
          name,
          values,
          type: (isCurrency ? 'currency' : isPercent ? 'percent' : 'number') as 'currency' | 'number' | 'percent'
        }
      }).filter(m => m.name && m.values.some(v => v.value !== 0))


      // Extract period dates if available
      const periodStartField = keys.find(k => k.toLowerCase().includes('period_start') || k.toLowerCase().includes('start'))
      const periodEndField = keys.find(k => k.toLowerCase().includes('period_end') || k.toLowerCase().includes('end'))

      if (metrics.length > 0) {
        return {
          metrics,
          periods: yearColumns.sort(),
          periodStart: periodStartField ? String(firstRow[periodStartField]) : undefined,
          periodEnd: periodEndField ? String(firstRow[periodEndField]) : undefined
        }
      }
    }
  }

  // Strategy 2: Look for a "period" column where values are years
  const periodField = keys.find(k => PERIOD_FIELD_PATTERN.test(k.toLowerCase()))
  if (periodField) {
    const uniquePeriods = [...new Set(rows.map(r => String(r[periodField])))]
    if (uniquePeriods.length >= 2 && uniquePeriods.some(p => YEAR_PATTERN.test(p) || YEAR_LIKE_PATTERN.test(p))) {
      // Pivot the data to get metrics by period
      const valueFields = keys.filter(k => {
        const val = firstRow[k]
        return (typeof val === 'number' || !isNaN(parseFloat(val))) && k !== periodField
      })

      if (valueFields.length > 0) {
        const metrics = valueFields.map(field => {
          const values = uniquePeriods.map(period => {
            const row = rows.find(r => String(r[periodField]) === period)
            return { period, value: row ? (parseFloat(row[field]) || 0) : 0 }
          })
          const lowerField = field.toLowerCase()
          const isCurrency = lowerField.includes('revenue') || lowerField.includes('sales') ||
                            lowerField.includes('cost') || lowerField.includes('profit') ||
                            lowerField.includes('amount')
          const isPercent = lowerField.includes('margin') || lowerField.includes('rate')
          return {
            name: field.replace(/_/g, ' '),
            values,
            type: (isCurrency ? 'currency' : isPercent ? 'percent' : 'number') as 'currency' | 'number' | 'percent'
          }
        })
        if (metrics.length > 0) {
          return { metrics, periods: uniquePeriods.sort() }
        }
      }
    }
  }

  // Strategy 3: Check if this looks like period data by checking for numeric columns with year-like names in row values
  // This handles cases where the data format is different
  if (rows.length >= 2) {
    const numericKeys = keys.filter(k => typeof firstRow[k] === 'number')
    // Future: could detect periods from numeric column patterns
  }

  return null
}

// Extract metrics from data rows
const extractMetricsFromData = (rows: any[], query: string): DetectedMetric[] => {
  if (!rows || rows.length === 0) return []

  // Normalize MCP data for better field detection
  const normalizedRows = normalizeMcpData(rows)

  const metrics: DetectedMetric[] = []
  const firstRow = normalizedRows[0]
  const keys = Object.keys(firstRow)

  // If only one row, each numeric field could be a metric
  if (rows.length === 1) {
    for (const key of keys) {
      const value = parseFloat(firstRow[key])
      if (!isNaN(value) && isValueField(key)) {
        const isCurrency = key.toLowerCase().includes('revenue') ||
                          key.toLowerCase().includes('amount') ||
                          key.toLowerCase().includes('total') ||
                          key.toLowerCase().includes('sales') ||
                          key.toLowerCase().includes('profit')
        metrics.push({
          label: key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim(),
          value,
          type: isCurrency ? 'currency' : 'number'
        })
      }
    }
    return metrics.slice(0, 4)
  }

  // For multiple rows, sum up value fields
  const structure = analyzeDataStructure(normalizedRows, query)
  for (const valueField of structure.valueFields.slice(0, 3)) {
    const sum = normalizedRows.reduce((acc, r) => acc + (parseFloat(r[valueField]) || 0), 0)
    if (sum > 0) {
      const isCurrency = valueField.toLowerCase().includes('revenue') ||
                        valueField.toLowerCase().includes('amount') ||
                        valueField.toLowerCase().includes('total') ||
                        valueField.toLowerCase().includes('sales')
      metrics.push({
        label: `Total ${valueField.replace(/_/g, ' ')}`,
        value: sum,
        type: isCurrency ? 'currency' : 'number'
      })
    }
  }

  // Add count metric
  metrics.push({
    label: 'Records',
    value: rows.length,
    type: 'number'
  })

  return metrics.slice(0, 4)
}

// Extract human-readable key findings from data for card display
const extractKeyFindings = (data: any[], query: string): string[] => {
  if (!data || data.length === 0) return ['No data returned']

  const findings: string[] = []

  try {
    // 1. Record count
    findings.push(`${data.length.toLocaleString()} records returned`)

    // 2. Extract top numeric metrics
    const metrics = extractMetricsFromData(data, query)
    for (const m of metrics.slice(0, 3)) {
      if (m.label === 'Records') continue // Skip since we already show record count
      const formatted = m.type === 'currency' ? formatCurrency(m.value) : formatNumber(m.value)
      findings.push(`${m.label}: ${formatted}`)
    }
  } catch (err) {
    console.error('[extractKeyFindings] Error:', err)
    if (findings.length === 0) {
      findings.push(`${data.length} records returned`)
    }
  }

  return findings.slice(0, 4)
}

// Case-insensitive field lookup helper
const getFieldValue = (row: any, fieldNames: string[]): any => {
  if (!row || typeof row !== 'object') return undefined

  // First try exact match
  for (const name of fieldNames) {
    if (row[name] !== undefined && row[name] !== null) {
      return row[name]
    }
  }

  // Then try case-insensitive match
  const rowKeys = Object.keys(row)
  for (const name of fieldNames) {
    const nameLower = name.toLowerCase()
    for (const key of rowKeys) {
      if (key.toLowerCase() === nameLower) {
        return row[key]
      }
    }
  }

  // Finally try partial match (contains)
  for (const name of fieldNames) {
    const nameLower = name.toLowerCase()
    for (const key of rowKeys) {
      if (key.toLowerCase().includes(nameLower) || nameLower.includes(key.toLowerCase())) {
        return row[key]
      }
    }
  }

  return undefined
}

// Get numeric field value with case-insensitive lookup
const getNumericField = (row: any, fieldNames: string[]): number => {
  const value = getFieldValue(row, fieldNames)
  if (value === undefined || value === null) return 0
  const num = Number(value)
  return isNaN(num) ? 0 : num
}

// Get string field value with case-insensitive lookup
const getStringField = (row: any, fieldNames: string[], defaultValue: string = ''): string => {
  const value = getFieldValue(row, fieldNames)
  if (value === undefined || value === null) return defaultValue
  return String(value)
}

// Parse tool output to extract data
const parseToolOutput = (output: any): any => {

  if (!output) {
    return null
  }

  if (typeof output === 'string') {
    try {
      const parsed = JSON.parse(output)
      return parsed
    } catch (e) {
      return null
    }
  }

  return output
}

const isKlaviyoMcpTool = (toolName?: string): boolean => {
  if (!toolName) return false
  const normalized = toolName.replace(/^mcp__payhelm__/, '').toLowerCase()
  return normalized.includes('klaviyo')
}

/** Pull Klaviyo API error details from MCP tool output (error results or metadata). */
const extractKlaviyoErrorsFromParsed = (parsed: any): { message: string; errors: Array<{ detail?: string; title?: string; code?: string }> } | null => {
  if (!parsed) return null

  const collectFromErrorsArray = (errors: any[]) => {
    const mapped = errors
      .filter((e) => e && typeof e === 'object')
      .map((e) => ({
        detail: e.detail || e.message,
        title: e.title,
        code: e.code,
      }))
      .filter((e) => e.detail || e.title)
    if (!mapped.length) return null
    return {
      message: mapped.map((e) => e.detail || e.title).join('; '),
      errors: mapped,
    }
  }

  if (parsed.errors && Array.isArray(parsed.errors)) {
    const fromDirect = collectFromErrorsArray(parsed.errors)
    if (fromDirect) return fromDirect
  }

  if (parsed.metadata?.errors && Array.isArray(parsed.metadata.errors)) {
    const fromMeta = collectFromErrorsArray(parsed.metadata.errors)
    if (fromMeta) return fromMeta
  }

  if (parsed.metadata?.details?.errors && Array.isArray(parsed.metadata.details.errors)) {
    const fromDetails = collectFromErrorsArray(parsed.metadata.details.errors)
    if (fromDetails) return fromDetails
  }

  const results = parsed.results || (Array.isArray(parsed) ? parsed : null)
  if (Array.isArray(results)) {
    for (const row of results) {
      if (row?.id === 'error' || row?.metadata?.error) {
        const text = row?.text || row?.metadata?.error || ''
        const fromMeta = row?.metadata?.details?.errors
        if (fromMeta && Array.isArray(fromMeta)) {
          const collected = collectFromErrorsArray(fromMeta)
          if (collected) {
            return { message: collected.message || text, errors: collected.errors }
          }
        }
        if (text) {
          return { message: String(text), errors: [{ detail: String(text) }] }
        }
      }
    }
  }

  if (parsed.error || parsed.Error) {
    const msg = String(parsed.error || parsed.Error)
    return { message: msg, errors: [{ detail: msg }] }
  }

  return null
}

// Convert columns/rows format to array of objects
const convertColumnsRowsToObjects = (columns: string[], rows: any[][]): any[] => {
  if (rows.length === 0) return []

  // Check if rows are already objects (not arrays)
  const firstRow = rows[0]
  if (firstRow && typeof firstRow === 'object' && !Array.isArray(firstRow)) {
    return rows
  }

  return rows.map((row, rowIdx) => {
    if (!Array.isArray(row)) {
      return row
    }
    const obj: any = {}
    columns.forEach((col, idx) => {
      obj[col] = row[idx]
    })
    return obj
  })
}

// Deep search for columns/rows format in nested object
// Returns either columns/rows for conversion, or just the rows if they're already objects
const findColumnsRows = (obj: any, depth: number = 0): { columns: string[], rows: any[][] } | null => {
  if (depth > 4 || !obj || typeof obj !== 'object') return null

  // Check if this object has columns/rows
  if (obj.columns && Array.isArray(obj.columns) && obj.rows && Array.isArray(obj.rows)) {

    if (obj.rows.length > 0) {
      const firstRow = obj.rows[0]

      // Case 1: rows are arrays - classic columns/rows format
      if (Array.isArray(firstRow)) {
        return { columns: obj.columns, rows: obj.rows }
      }

      // Case 2: rows are objects - convert using column order
      // This handles the case where MCP returns rows as objects but provides columns for ordering
      if (typeof firstRow === 'object' && firstRow !== null) {
        const convertedRows = obj.rows.map((row: any) => {
          return obj.columns.map((col: string) => row[col])
        })
        return { columns: obj.columns, rows: convertedRows }
      }
    }
  }

  // Search nested objects
  for (const key of Object.keys(obj)) {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      const found = findColumnsRows(obj[key], depth + 1)
      if (found) return found
    }
  }

  return null
}

// Check if an object looks like report/search metadata (not actual data)
const looksLikeReportMetadata = (obj: any): boolean => {
  if (!obj || typeof obj !== 'object') return false
  const keys = Object.keys(obj)
  // Report metadata typically has these fields
  const metadataIndicators = ['id', 'title', 'icon_url', 'iconUrl', 'icon', 'text', 'description', 'url', 'report', 'reportUrl']
  const hasMetadataFields = metadataIndicators.filter(f => keys.includes(f)).length >= 2
  // Actual data typically has these fields
  const dataIndicators = ['name', 'revenue', 'sales', 'orders', 'total', 'count', 'price', 'quantity', 'amount', 'profit', 'margin', 'rate']
  const hasDataFields = dataIndicators.some(f => keys.some(k => k.toLowerCase().includes(f)))
  return hasMetadataFields && !hasDataFields
}

// Check if metadata object contains actual business data (sales, traffic, products, etc.)
const isBusinessDataMetadata = (metadata: any): boolean => {
  if (!metadata || typeof metadata !== 'object') return false
  const keys = Object.keys(metadata)
  // Business data indicators - include all types
  const businessFields = [
    // Sales/orders fields
    'sales', 'revenue', 'orders', 'total', 'count', 'date', 'profit', 'tax', 'shipping',
    'items_sold', 'gross_sales', 'net_sales', 'discount', 'margin', 'amount', 'value', 'spent',
    // Customer fields
    'customer', 'email', 'name', 'first_name', 'last_name', 'lifetime', 'vip', 'segment',
    'total_spent', 'order_count', 'average_order', 'last_order', 'phone', 'address',
    // Traffic fields
    'pageviews', 'sessions', 'visitors', 'date_trunc', 'total_pageviews', 'unique_visitors',
    'carts', 'checkouts', 'conversions', 'bounce',
    // Product fields
    'product', 'sku', 'quantity', 'price', 'inventory', 'stock',
    // Time period fields
    'period', 'month', 'year', 'week',
    // Google Ads fields
    'clicks', 'impressions', 'cost', 'conversions', 'ctr', 'cpc', 'campaign', 'rows', 'customers',
    // Inventory fields
    'available', 'on_hand', 'reserved', 'variants', 'brands'
  ]
  const hasBusinessData = businessFields.some(f => keys.some(k => k.toLowerCase().includes(f)))
  return hasBusinessData
}

// Flatten Google Ads campaign data for charting
// Input: { rows: [{ campaign: { name }, metrics: { clicks, impressions, costMicros, conversions } }] }
// Output: [{ name, clicks, impressions, cost, conversions }]
const flattenGoogleAdsData = (data: any): any[] | null => {
  if (!data || !data.rows || !Array.isArray(data.rows)) return null

  const flattened = data.rows.map((row: any) => {
    const result: any = {}

    // Extract campaign name
    if (row.campaign?.name) {
      result.name = row.campaign.name
    } else if (row.adGroup?.name) {
      result.name = row.adGroup.name
    }

    // Extract metrics
    if (row.metrics) {
      if (row.metrics.clicks) result.clicks = parseInt(row.metrics.clicks) || 0
      if (row.metrics.impressions) result.impressions = parseInt(row.metrics.impressions) || 0
      if (row.metrics.conversions !== undefined) result.conversions = parseFloat(row.metrics.conversions) || 0
      // Convert costMicros to dollars
      if (row.metrics.costMicros) {
        result.cost = (parseInt(row.metrics.costMicros) || 0) / 1000000
      }
      // Calculate CTR and CPC if we have the data
      if (result.impressions > 0 && result.clicks !== undefined) {
        result.ctr = ((result.clicks / result.impressions) * 100).toFixed(2)
      }
      if (result.clicks > 0 && result.cost !== undefined) {
        result.cpc = (result.cost / result.clicks).toFixed(2)
      }
    }

    return result
  }).filter((row: any) => row.name) // Only keep rows with names

  return flattened.length > 0 ? flattened : null
}

// Flatten traffic summary data for visualization
// Input: { summary: { totalSessions, totalVisitors, ... }, conversionRates: { ... } }
// Output: array of metric objects for funnel visualization
const flattenTrafficSummaryData = (data: any): any[] | null => {
  if (!data) return null

  const metrics: any[] = []

  // Check for summary object
  if (data.summary) {
    const s = data.summary
    if (s.totalVisitors) metrics.push({ stage: 'Visitors', value: s.totalVisitors, type: 'count' })
    if (s.totalSessions) metrics.push({ stage: 'Sessions', value: s.totalSessions, type: 'count' })
    if (s.totalPageviews) metrics.push({ stage: 'Pageviews', value: s.totalPageviews, type: 'count' })
    if (s.totalCarts) metrics.push({ stage: 'Added to Cart', value: s.totalCarts, type: 'count' })
    if (s.totalCheckouts) metrics.push({ stage: 'Checkouts', value: s.totalCheckouts, type: 'count' })
    if (s.totalOrders) metrics.push({ stage: 'Orders', value: s.totalOrders, type: 'count' })
    if (s.totalSales) metrics.push({ stage: 'Revenue', value: s.totalSales, type: 'currency' })
  }

  // Check for conversion rates
  if (data.conversionRates) {
    const cr = data.conversionRates
    if (cr.visitorToCart) metrics.push({ stage: 'Visitor → Cart', value: parseFloat(cr.visitorToCart) || 0, type: 'percent' })
    if (cr.cartToCheckout) metrics.push({ stage: 'Cart → Checkout', value: parseFloat(cr.cartToCheckout) || 0, type: 'percent' })
    if (cr.checkoutToOrder) metrics.push({ stage: 'Checkout → Order', value: parseFloat(cr.checkoutToOrder) || 0, type: 'percent' })
    if (cr.overallConversion) metrics.push({ stage: 'Overall Conversion', value: parseFloat(cr.overallConversion) || 0, type: 'percent' })
  }

  return metrics.length > 0 ? metrics : null
}

// Extract actual data from report metadata object - looks for nested columns/rows, data, metadata, etc.
const extractDataFromReportResult = (result: any): any[] | null => {
  if (!result || typeof result !== 'object') return null

  // Check for columns/rows format inside the result
  if (result.columns && Array.isArray(result.columns) && result.rows && Array.isArray(result.rows)) {
    const firstRow = result.rows[0]
    if (Array.isArray(firstRow)) {
      return convertColumnsRowsToObjects(result.columns, result.rows)
    }
    if (typeof firstRow === 'object') {
      return result.rows
    }
  }

  // Check for data field
  if (result.data) {
    if (Array.isArray(result.data) && result.data.length > 0 && typeof result.data[0] === 'object') {
      return result.data
    }
    if (typeof result.data === 'object' && result.data.columns && result.data.rows) {
      const firstRow = result.data.rows[0]
      if (Array.isArray(firstRow)) {
        return convertColumnsRowsToObjects(result.data.columns, result.data.rows)
      }
      return result.data.rows
    }
  }

  // Check for metrics field
  if (result.metrics && Array.isArray(result.metrics) && result.metrics.length > 0) {
    return result.metrics
  }

  // Check for items field
  if (result.items && Array.isArray(result.items) && result.items.length > 0) {
    return result.items
  }

  // Check if 'text' field contains JSON data (MCP sometimes embeds data as JSON string in text)
  if (result.text && typeof result.text === 'string' && result.text.length > 50) {
    // Look for JSON embedded in the text field
    const jsonMatch = result.text.match(/\{[\s\S]*"(?:columns|data|results|rows)"[\s\S]*\}/)
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0])
        // Recursively extract from the parsed JSON
        const nestedData = extractDataFromReportResult(parsed)
        if (nestedData) return nestedData
        // Check if parsed is directly an array
        if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object') {
          return parsed
        }
      } catch (e) {
        // Not valid JSON, continue
      }
    }
  }

  return null
}

// Extract metadata objects from MCP search results as the actual data rows
// MCP returns: { results: [{ id, title, text, metadata: {actual data} }, ...] }
const extractMetadataFromMCPResults = (results: any[]): any[] | null => {
  if (!Array.isArray(results) || results.length === 0) return null

  // Check if this looks like MCP search results (items have id, title, text, metadata)
  const first = results[0]
  if (!first || typeof first !== 'object') return null

  const hasMCPStructure = 'id' in first && 'title' in first && 'metadata' in first

  if (!hasMCPStructure) return null


  // Check if metadata contains Google Ads data (rows with campaign/metrics)
  for (const item of results) {
    if (item.metadata?.rows && Array.isArray(item.metadata.rows)) {
      const firstRow = item.metadata.rows[0]
      if (firstRow && (firstRow.campaign || firstRow.adGroup) && firstRow.metrics) {
        const flattened = flattenGoogleAdsData(item.metadata)
        if (flattened && flattened.length > 0) {
          return flattened
        }
      }
    }
    // Check for traffic summary data
    if (item.metadata?.summary && (item.metadata.summary.totalSessions || item.metadata.summary.totalVisitors)) {
      const flattened = flattenTrafficSummaryData(item.metadata)
      if (flattened && flattened.length > 0) {
        return flattened
      }
    }
  }

  // Filter out non-data items (payhelm header, consumed_credits, stats summaries) and extract metadata
  const dataRows = results
    .filter(item => {
      // Skip header items and stats summaries
      if (item.id === 'payhelm' || item.id === 'consumed_credits') {
        return false
      }
      if (item.id?.includes('-stats-')) {
        return false
      }
      // Include items with metadata (be permissive - any metadata is potentially useful)
      if (!item.metadata || typeof item.metadata !== 'object') {
        return false
      }
      // Log what we're checking
      return true // Accept any item with metadata
    })
    .map(item => {
      // Clean and normalize the metadata
      const cleaned: any = {}


      // SIMPLIFIED: Just copy ALL metadata fields, handling types appropriately
      for (const [key, value] of Object.entries(item.metadata)) {
        const valType = typeof value

        // Skip null/undefined
        if (value === null || value === undefined) {
          continue
        }

        // Primitives: numbers, strings, booleans - add directly
        if (valType === 'number' || valType === 'boolean') {
          cleaned[key] = value
        }
        else if (valType === 'string') {
          // Try to parse string numbers
          if (/^-?[\d,]+(\.\d+)?$/.test(value)) {
            const parsed = parseFloat(value.replace(/,/g, ''))
            cleaned[key] = parsed
          } else {
            cleaned[key] = value
          }
          // Special handling for date_trunc
          if (key === 'date_trunc') {
            cleaned['date'] = value.split('T')[0]
          }
        }
        // Arrays
        else if (Array.isArray(value)) {
          if (value.length > 0 && (typeof value[0] === 'string' || typeof value[0] === 'number')) {
            cleaned[key] = value
          } else if (value.length > 0) {
            cleaned[key] = `${value.length} items`
          }
        }
        // Nested objects - flatten one level
        else if (valType === 'object') {
          const nested = value as Record<string, any>
          // Try to extract .value or .name first
          if (nested.value !== undefined) {
            cleaned[key] = nested.value
          } else if (nested.name !== undefined) {
            cleaned[key] = nested.name
          } else {
            // Flatten all primitive fields from nested object
            let flattenCount = 0
            for (const [nk, nv] of Object.entries(nested)) {
              if (nv !== null && typeof nv !== 'object') {
                cleaned[`${key}_${nk}`] = nv
                flattenCount++
              } else if (typeof nv === 'object' && nv !== null && !Array.isArray(nv)) {
                // Go one more level deep for nested.nested
                for (const [nnk, nnv] of Object.entries(nv as Record<string, any>)) {
                  if (nnv !== null && typeof nnv !== 'object') {
                    cleaned[`${key}_${nk}_${nnk}`] = nnv
                    flattenCount++
                  }
                }
              }
            }
          }
        }
      }


      // Add title context if useful
      if (item.title && !cleaned.name && !cleaned.title) {
        cleaned._title = item.title
      }

      return cleaned
    })

  if (dataRows.length === 0) {
    return null
  }


  return dataRows
}

// Deep search for data array in nested object
const findDataArray = (obj: any, depth: number = 0): any[] | null => {
  if (depth > 4 || !obj || typeof obj !== 'object') return null

  // First check if this object IS a columns/rows structure and convert it
  if (obj.columns && Array.isArray(obj.columns) && obj.rows && Array.isArray(obj.rows)) {
    const firstDataRow = obj.rows[0]
    if (Array.isArray(firstDataRow)) {
      return convertColumnsRowsToObjects(obj.columns, obj.rows)
    }
  }

  // Check common array fields
  const arrayFields = ['results', 'data', 'rows', 'items', 'records', 'metrics']
  for (const field of arrayFields) {
    const fieldValue = obj[field]
    if (!fieldValue) continue

    // If it's an array of objects, check if they're report metadata or actual data
    if (Array.isArray(fieldValue) && fieldValue.length > 0) {
      const first = fieldValue[0]
      if (typeof first === 'object' && first !== null && !Array.isArray(first)) {
        // Check if this looks like MCP search results (id, title, metadata structure)
        if ('id' in first && 'title' in first && 'metadata' in first) {
          // Extract metadata objects as the actual data
          const metadataRows = extractMetadataFromMCPResults(fieldValue)
          if (metadataRows && metadataRows.length > 0) {
            return metadataRows
          }
        }

        // Check if this looks like report metadata array (legacy format)
        if (looksLikeReportMetadata(first)) {
          // Try to extract data from inside each result
          for (const result of fieldValue) {
            const nestedData = extractDataFromReportResult(result)
            if (nestedData && nestedData.length > 0) {
              return nestedData
            }
          }
          // No nested data found - use the original array as fallback (metadata still has value)
          return fieldValue
        }
        // Not metadata, return as data
        return fieldValue
      }
      // If it's an array of arrays with a columns field, convert it
      if (Array.isArray(first) && obj.columns && Array.isArray(obj.columns)) {
        return convertColumnsRowsToObjects(obj.columns, fieldValue)
      }
    }

    // If it's an object that might contain columns/rows, check it
    if (typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
      if (fieldValue.columns && Array.isArray(fieldValue.columns) &&
          fieldValue.rows && Array.isArray(fieldValue.rows)) {
        const firstDataRow = fieldValue.rows[0]
        if (Array.isArray(firstDataRow)) {
          return convertColumnsRowsToObjects(fieldValue.columns, fieldValue.rows)
        }
      }
    }
  }

  // Search nested objects
  for (const key of Object.keys(obj)) {
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      const found = findDataArray(obj[key], depth + 1)
      if (found) return found
    }
  }

  return null
}

// Clean a row object - flatten metadata if present, otherwise keep as-is
const cleanRow = (row: any): any => {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return row

  const cleaned: any = {}

  // Check if this is an MCP result structure: { id, title, text, metadata }
  if (row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)) {

    // Extract all fields from metadata using same logic as extractMetadataFromMCPResults
    for (const [key, value] of Object.entries(row.metadata)) {
      if (value === null || value === undefined) continue

      const valType = typeof value
      if (valType === 'number' || valType === 'boolean') {
        cleaned[key] = value
      } else if (valType === 'string') {
        if (/^-?[\d,]+(\.\d+)?$/.test(value)) {
          cleaned[key] = parseFloat(value.replace(/,/g, ''))
        } else {
          cleaned[key] = value
        }
        if (key === 'date_trunc') cleaned['date'] = value.split('T')[0]
      } else if (Array.isArray(value)) {
        if (value.length > 0 && (typeof value[0] === 'string' || typeof value[0] === 'number')) {
          cleaned[key] = value
        } else if (value.length > 0) {
          cleaned[key] = `${value.length} items`
        }
      } else if (valType === 'object') {
        const nested = value as Record<string, any>
        if (nested.value !== undefined) {
          cleaned[key] = nested.value
        } else if (nested.name !== undefined) {
          cleaned[key] = nested.name
        } else {
          for (const [nk, nv] of Object.entries(nested)) {
            if (nv !== null && typeof nv !== 'object') {
              cleaned[`${key}_${nk}`] = nv
            }
          }
        }
      }
    }

    // Keep title for context
    if (row.title && !cleaned.name && !cleaned.title) {
      cleaned._title = row.title
    }
    return cleaned
  }

  for (const [key, value] of Object.entries(row)) {
    // Skip structural fields that aren't data (but NOT metadata - handled above)
    const keyLower = key.toLowerCase()
    const metadataFields = [
      '_meta', 'meta', '__typename', 'columns', 'rows', 'query',
      'report', 'reporturl', 'report_url', 'reportlink', 'report_link',
      'icon', 'icon_url', 'iconurl'
    ]
    if (metadataFields.includes(keyLower)) {
      continue
    }

    // Keep primitive values as-is
    if (value === null || value === undefined || typeof value !== 'object') {
      cleaned[key] = value
      continue
    }

    // Handle arrays - keep simple arrays, summarize complex ones
    if (Array.isArray(value)) {
      if (value.length === 0) {
        cleaned[key] = null
      } else if (typeof value[0] === 'string' || typeof value[0] === 'number') {
        cleaned[key] = value // Keep simple arrays
      } else {
        cleaned[key] = `${value.length} items`
      }
      continue
    }

    // Handle objects - extract meaningful value or flatten one level
    const nested = value as Record<string, any>
    if (nested.name !== undefined) {
      cleaned[key] = nested.name
    } else if (nested.value !== undefined) {
      cleaned[key] = nested.value
    } else if (nested.title !== undefined) {
      cleaned[key] = nested.title
    } else {
      // Flatten one level
      for (const [nestedKey, nestedValue] of Object.entries(nested)) {
        if (typeof nestedValue !== 'object' || nestedValue === null) {
          cleaned[`${key}_${nestedKey}`] = nestedValue
        }
      }
    }
  }

  return cleaned
}

// Extract actual data array from nested response structure - IMPROVED
const extractDataArray = (parsed: any): any[] => {
  if (!parsed) {
    return []
  }

  // Helper to check if array contains data objects (not just primitives)
  const isDataArray = (arr: any[]): boolean => {
    if (arr.length === 0) return false
    const first = arr[0]
    return typeof first === 'object' && first !== null && !Array.isArray(first)
  }

  // Helper to check if an array looks like column headers (all strings, common header names)
  const looksLikeHeaders = (arr: any[]): boolean => {
    if (!Array.isArray(arr) || arr.length === 0) return false
    // Check if all items are strings
    if (!arr.every(item => typeof item === 'string')) return false
    // Check for common header patterns
    const lowerArr = arr.map(s => s.toLowerCase())
    const commonHeaders = ['name', 'date', 'id', 'title', 'value', 'total', 'count', 'metric', 'label', 'category', 'product', 'customer', 'order', 'revenue', 'sales', 'amount', 'quantity', 'status', 'type']
    return lowerArr.some(item => commonHeaders.some(h => item.includes(h))) || lowerArr.some(item => /20\d{2}/.test(item))
  }

  // Already an array of objects - check if it's actual data or report metadata
  if (Array.isArray(parsed) && isDataArray(parsed)) {
    // Check if this looks like MCP search results (id, title, metadata structure)
    const first = parsed[0]
    if (first && 'id' in first && 'title' in first && 'metadata' in first) {
      const metadataRows = extractMetadataFromMCPResults(parsed)
      if (metadataRows && metadataRows.length > 0) {
        return metadataRows.map(row => cleanRow(row))
      }
    }

    // Check if this looks like report metadata (legacy format)
    if (looksLikeReportMetadata(parsed[0])) {
      // Try to extract actual data from inside the results
      for (const result of parsed) {
        const nestedData = extractDataFromReportResult(result)
        if (nestedData && nestedData.length > 0) {
          return nestedData.map(row => cleanRow(row))
        }
      }
      // No nested data found - use the original array as fallback (metadata still has value)
      return parsed.map(row => cleanRow(row))
    } else {
      return parsed.map(row => cleanRow(row))
    }
  }

  // Handle array of arrays where first row might be headers
  if (Array.isArray(parsed) && parsed.length >= 2 && Array.isArray(parsed[0]) && Array.isArray(parsed[1])) {
    const firstRow = parsed[0]
    const secondRow = parsed[1]
    // If first row looks like headers and second row has different types or numbers
    if (looksLikeHeaders(firstRow) && (secondRow.some((v: any) => typeof v === 'number') || !looksLikeHeaders(secondRow))) {
      const columns = firstRow as string[]
      const dataRows = parsed.slice(1) as any[][]
      const converted = convertColumnsRowsToObjects(columns, dataRows)
      return converted.map(row => cleanRow(row))
    }
  }

  // Try to find columns/rows format anywhere in the object
  const colsRows = findColumnsRows(parsed)
  if (colsRows) {
    const converted = convertColumnsRowsToObjects(colsRows.columns, colsRows.rows)
    return converted.map(row => cleanRow(row))
  }

  // Try to find a data array anywhere in the object
  const dataArr = findDataArray(parsed)
  if (dataArr && dataArr.length > 0) {
    return dataArr.map(row => cleanRow(row))
  }

  // FALLBACK: If we have a columns array and a rows array at the top level, use them even if rows aren't perfect format
  if (parsed.columns && Array.isArray(parsed.columns) && parsed.rows && Array.isArray(parsed.rows) && parsed.rows.length > 0) {
    const firstRow = parsed.rows[0]

    // If rows are arrays of values
    if (Array.isArray(firstRow)) {
      const converted = convertColumnsRowsToObjects(parsed.columns, parsed.rows)
      return converted.map(row => cleanRow(row))
    }

    // If rows are already objects, just use them
    if (typeof firstRow === 'object' && firstRow !== null) {
      return parsed.rows.map((row: any) => cleanRow(row))
    }
  }

  // FALLBACK 2: Look for any "results" or "data" field that's an object with columns/rows
  for (const field of ['results', 'data', 'response', 'output']) {
    const fieldData = parsed[field]
    if (fieldData && typeof fieldData === 'object') {
      if (fieldData.columns && Array.isArray(fieldData.columns) && fieldData.rows && Array.isArray(fieldData.rows)) {
        const firstRow = fieldData.rows[0]
        if (Array.isArray(firstRow)) {
          const converted = convertColumnsRowsToObjects(fieldData.columns, fieldData.rows)
          return converted.map(row => cleanRow(row))
        } else if (typeof firstRow === 'object') {
          return fieldData.rows.map((row: any) => cleanRow(row))
        }
      }
    }
  }

  // Check for direct data at top level (object with data-like fields)
  if (typeof parsed === 'object' && !Array.isArray(parsed)) {
    const keys = Object.keys(parsed).filter(k =>
      !['_meta', 'meta', 'query', 'report', 'url', 'error', 'message', 'status', 'columns', 'rows'].includes(k.toLowerCase())
    )
    const hasNumericValues = keys.some(k => typeof parsed[k] === 'number')
    const hasStringName = keys.some(k => ['name', 'metric', 'title', 'category', 'product', 'label'].includes(k.toLowerCase()))


    if (hasNumericValues && (hasStringName || keys.length >= 3)) {
      return [cleanRow(parsed)]
    }
  }

  return []
}

// Fields to exclude from display (metadata-like and structural fields)
const EXCLUDE_FIELDS = ['_meta', '__typename', 'reportUrl', 'report_url', 'columns', 'rows', 'query', 'meta', 'metadata']

// Extract a clean PayHelm report URL from a string that may contain markdown or other text
const extractPayhelmUrl = (str: string): string | undefined => {
  const match = str.match(/https?:\/\/[^\s"'<>()]+payhelm\.com\/reports\/[^\s"'<>()]+/i)
  return match ? match[0] : undefined
}

// Extract report URL from data - searches deeply for any PayHelm report URL
const extractReportUrl = (data: any): string | undefined => {
  if (!data) return undefined

  if (typeof data === 'string') return extractPayhelmUrl(data)
  if (typeof data !== 'object') return undefined

  // Check results array items (MCP returns { results: [{ text, url, ... }] })
  if (data.results && Array.isArray(data.results)) {
    for (const result of data.results) {
      if (!result || typeof result !== 'object') continue
      for (const field of ['text', 'url', 'report', 'reportUrl', 'report_url', 'link']) {
        if (result[field] && typeof result[field] === 'string') {
          const url = extractPayhelmUrl(result[field])
          if (url) return url
        }
      }
    }
  }

  // Check direct fields
  const urlFields = ['report', 'reportUrl', 'reportLink', 'report_url', 'report_link', 'url', 'link', 'viewUrl', 'view_url']
  for (const field of urlFields) {
    const value = data[field]
    if (value && typeof value === 'string') {
      const url = extractPayhelmUrl(value)
      if (url) return url
    }
  }

  return undefined
}

// Generate a human-readable query description from tool name and input
const generateQueryDescription = (tool: string | undefined, toolInput: any): string => {
  if (!tool) return 'Query'

  // Strip MCP prefix for cleaner display
  const shortName = tool.replace(/^(mcp__payhelm__|payhelm__)/, '')

  // If there's a query in the input, use it
  if (toolInput?.query) {
    return toolInput.query
  }

  // Generate description based on tool name and input parameters
  const toolDescriptions: Record<string, (input: any) => string> = {
    'search': (input) => input?.query || 'Database search',
    'fetch': (input) => input?.id ? `Fetching record ${input.id}` : 'Fetching record',
    'get_klaviyo_accounts': () => 'Klaviyo connected accounts',
    'get_google_ads_accounts': () => 'Google Ads connected accounts',
    'list_google_ads_client_accounts': (input) =>
      input?.managerCustomerId ? `Google Ads clients under manager ${input.managerCustomerId}` : 'Google Ads clients under manager',
    'get_facebook_ads_accounts': () => 'Facebook Ads connected accounts',
    'get_openai_ads_accounts': () => 'OpenAI Ads connected account',
    'get_tiktok_accounts': () => 'TikTok connected accounts',
    'get_pinterest_accounts': () => 'Pinterest connected accounts',
    'get_bing_ads_accounts': () => 'Bing Ads connected accounts',
    'search_google_ads': (input) => input?.query || 'Google Ads data',
    'search_facebook_ads': (input) => input?.query || 'Facebook Ads data',
    'create_google_ads_pmax_campaign': (input) => input?.campaignName ? `Create PMax: ${input.campaignName}` : 'Create Performance Max campaign',
    'create_google_ads_conversion': (input) => input?.name ? `Create conversion: ${input.name}` : 'Create conversion action',
    'modify_google_ads_conversion': (input) => (input?.name ? `Update conversion: ${input.name}` : 'Update conversion action'),
    'remove_google_ads_conversion': (input) =>
      input?.resourceName || input?.conversionActionId
        ? `Remove conversion: ${input.resourceName || input.conversionActionId}`
        : 'Remove conversion action',
    'search_klaviyo': (input) => input?.query || 'Klaviyo data',
    'search_tiktok': (input) => input?.query || 'TikTok data',
    'search_pinterest': (input) => input?.query || 'Pinterest data',
    'get_mailchimp_content': (input) => input?.campaignId ? `Mailchimp content (campaign ${input.campaignId})` : 'Mailchimp email content',
    'update_mailchimp_content': (input) => input?.findUrl ? `Replace URL in Mailchimp: ${input.findUrl}` : 'Update Mailchimp email URL',
    'get_mailchimp_automation_emails': (input) => input?.automationId ? `Automation emails (${input.automationId})` : 'Mailchimp automation emails',
    'get_klaviyo_template': (input) => input?.templateId ? `Klaviyo template (${input.templateId})` : 'Klaviyo template content',
    'update_klaviyo_template': (input) => input?.findUrl ? `Replace URL in Klaviyo template: ${input.findUrl}` : 'Update Klaviyo template URL',
    'get_klaviyo_flow_messages': (input) => input?.flowId ? `Flow messages (${input.flowId})` : 'Klaviyo flow messages',
    'get_traffic_data': (input) => input?.date_range ? `Traffic data (${input.date_range})` : 'Traffic data',
    'get_traffic_metrics': () => 'Traffic metrics',
    'get_traffic_summary': () => 'Traffic summary',
    'get_ads_performance': (input) => input?.platform ? `${input.platform} ads performance` : 'Ads performance',
    'get_inventory_products': () => 'Inventory products',
    'get_inventory_variants': () => 'Inventory variants',
    'get_inventory_summary': () => 'Inventory summary',
    'get_current_shipping_settings': () => 'Shipping settings',
    'insure_shipment': (input) => input?.order_id ? `Insurance for order ${input.order_id}` : 'Shipment insurance',
    'void_insurance': (input) => input?.order_id ? `Void insurance for order ${input.order_id}` : 'Void insurance',
    'export_to_email': (input) => input?.email ? `Export to ${input.email}` : 'Email export',
    'export_to_csv': () => 'CSV export',
    'export_to_google_drive': () => 'Google Drive export',
  }

  // Try to get a description from the map
  const descFn = toolDescriptions[shortName]
  if (descFn) {
    return descFn(toolInput)
  }

  // Fallback: convert tool name to readable format
  // e.g., "get_traffic_data" -> "Get Traffic Data"
  const readable = shortName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())

  return readable
}

// Aggregate all tool results into unified data structure
const aggregateData = (steps: AgentStep[], thoughts: string[]): AggregatedData => {
  const data: AggregatedData = {
    revenue: { total: 0, trend: [], byCategory: [] },
    orders: { total: 0, trend: [] },
    products: { topProducts: [], totalProducts: 0 },
    customers: { total: 0, newCustomers: 0 },
    insights: [],
    reportUrls: [],
    rawResults: [],
    periodComparisons: [],
  }

  // Extract insights from thoughts
  thoughts.forEach(thought => {
    if (thought && thought.length > 20 && !thought.startsWith('Analyzing') && !thought.startsWith('Using ')) {
      data.insights.push(thought)
    }
  })

  // Process each tool step - use index for unique IDs
  // Each step is wrapped in try-catch so one bad step doesn't prevent subsequent steps from being processed
  let resultIndex = 0
  for (const step of steps) {
    if (step.status !== 'completed' || !step.toolOutput) continue

    try {
      const parsed = parseToolOutput(step.toolOutput)
      if (!parsed) continue

      const klaviyoErr = isKlaviyoMcpTool(step.tool) ? extractKlaviyoErrorsFromParsed(parsed) : null
      const isBareError =
        parsed.error ||
        parsed.Error ||
        (typeof parsed === 'object' && Object.keys(parsed).length === 1 && (parsed.error || parsed.Error))
      const isSingleRowError = Array.isArray(parsed) && parsed.length === 1 && parsed[0]?.error

      if (klaviyoErr || isBareError || isSingleRowError) {
        if (klaviyoErr || isKlaviyoMcpTool(step.tool)) {
          const queryDesc = generateQueryDescription(step.tool, step.toolInput)
          const errInfo = klaviyoErr || {
            message: String(parsed.error || parsed.Error || parsed[0]?.error || 'Klaviyo request failed'),
            errors: [{ detail: String(parsed.error || parsed.Error || parsed[0]?.error || 'Klaviyo request failed') }],
          }
          data.rawResults.push({
            query: queryDesc,
            data: [],
            tool: step.tool,
            toolInput: step.toolInput,
            apiError: errInfo.message,
            klaviyoErrors: errInfo.errors,
          })
        }
        continue
      }

      const reportUrl = extractReportUrl(parsed)

      if (reportUrl && !data.reportUrls.includes(reportUrl)) {
        data.reportUrls.push(reportUrl)
      }

      // Get results array using improved extraction
      const results = extractDataArray(parsed)

      // Deep clone results to ensure no shared references
      let clonedResults: any[]
      try {
        clonedResults = JSON.parse(JSON.stringify(results))
      } catch {
        // Fallback: use spread clone if JSON stringify fails (circular refs, etc.)
        clonedResults = results.map(r => ({ ...r }))
      }

      // Check if this is period comparison data
      const periodComparison = detectPeriodComparison(clonedResults)

      if (periodComparison && periodComparison.metrics.length > 0) {
        data.periodComparisons.push({
          query: generateQueryDescription(step.tool, step.toolInput) || 'Period Comparison',
          data: periodComparison,
          reportUrl,
        })
      }

      // Store ALL results for display - each MCP call gets its own visualization
      const isConfigTool = isConfigurationTool(step.tool)
      const queryDesc = generateQueryDescription(step.tool, step.toolInput)

      data.rawResults.push({
        query: queryDesc,
        data: clonedResults,
        reportUrl,
        isPeriodComparison: periodComparison && periodComparison.metrics.length > 0,
        isConfigurationData: isConfigTool,
        tool: step.tool,
        toolInput: step.toolInput,
      })

      // Also extract basic metrics for the summary section
      clonedResults.forEach(row => {
        const revenueValue = getNumericField(row, ['total', 'revenue', 'total_revenue', 'amount', 'sales', 'gross', 'net_sales', 'value', 'sum'])
        if (revenueValue > 0) {
          data.revenue.total += revenueValue
        }
        const orderValue = getNumericField(row, ['orders', 'order_count', 'num_orders', 'total_orders'])
        if (orderValue > 0) {
          data.orders.total += orderValue
        }
      })

      resultIndex++
    } catch (err) {
      console.error(`[AgentDashboard] Error processing step ${resultIndex} (${step.tool}):`, err)
      // Still try to add a minimal result so the card shows
      try {
        data.rawResults.push({
          query: generateQueryDescription(step.tool, step.toolInput),
          data: [],
          tool: step.tool,
          toolInput: step.toolInput,
        })
      } catch {}
      resultIndex++
    }
  }

  // Sort trends by date
  data.revenue.trend.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  data.orders.trend.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

  return data
}

// Format currency
const formatCurrency = (value: number): string => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`
  return `$${value.toFixed(2)}`
}

// Format number
const formatNumber = (value: number): string => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
  return value.toLocaleString()
}

// Metric Card Component - Compact
const MetricCard: React.FC<{
  icon: React.ComponentType<any>
  label: string
  value: string
  subValue?: string
  trend?: 'up' | 'down' | 'neutral'
  color: string
}> = ({ icon: Icon, label, value, subValue, trend, color }) => {
  const colorClasses: Record<string, { bg: string; icon: string; text: string }> = {
    blue: { bg: 'tw-bg-blue-50', icon: 'tw-text-blue-600', text: 'tw-text-blue-600' },
    green: { bg: 'tw-bg-green-50', icon: 'tw-text-green-600', text: 'tw-text-green-600' },
    purple: { bg: 'tw-bg-purple-50', icon: 'tw-text-purple-600', text: 'tw-text-purple-600' },
    amber: { bg: 'tw-bg-amber-50', icon: 'tw-text-amber-600', text: 'tw-text-amber-600' },
    indigo: { bg: 'tw-bg-indigo-50', icon: 'tw-text-indigo-600', text: 'tw-text-indigo-600' },
  }
  const colors = colorClasses[color] || colorClasses.blue

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-3 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
      <div className="tw-flex tw-items-center tw-gap-2">
        <div className={`tw-w-8 tw-h-8 tw-rounded-md ${colors.bg} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
          <Icon className={colors.icon} size={16} />
        </div>
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-text-lg tw-font-bold tw-text-gray-900 tw-leading-tight">{value}</div>
          <div className="tw-text-xs tw-text-gray-500 tw-truncate">{label}</div>
        </div>
        {trend && (
          <div className={`tw-flex tw-items-center tw-gap-0.5 tw-text-xs tw-font-medium tw-flex-shrink-0 ${trend === 'up' ? 'tw-text-green-600' : trend === 'down' ? 'tw-text-red-600' : 'tw-text-gray-500'}`}>
            {trend === 'up' ? <BiTrendingUp size={12} /> : trend === 'down' ? <BiTrendingDown size={12} /> : null}
            {subValue}
          </div>
        )}
      </div>
    </div>
  )
}

// Section Header Component - Compact with optional data source icon
const SectionHeader: React.FC<{
  icon: React.ComponentType<any>
  title: string
  subtitle?: string
  color: string
  action?: React.ReactNode
  dataSource?: string
}> = ({ icon: Icon, title, subtitle, color, action, dataSource }) => {
  const colorClasses: Record<string, { bg: string; icon: string; border: string }> = {
    blue: { bg: 'tw-bg-blue-50', icon: 'tw-text-blue-600', border: 'tw-border-blue-200' },
    green: { bg: 'tw-bg-green-50', icon: 'tw-text-green-600', border: 'tw-border-green-200' },
    purple: { bg: 'tw-bg-purple-50', icon: 'tw-text-purple-600', border: 'tw-border-purple-200' },
    amber: { bg: 'tw-bg-amber-50', icon: 'tw-text-amber-600', border: 'tw-border-amber-200' },
    indigo: { bg: 'tw-bg-indigo-50', icon: 'tw-text-indigo-600', border: 'tw-border-indigo-200' },
  }
  const colors = colorClasses[color] || colorClasses.blue

  // Get data source icon if provided
  const SourceIcon = dataSource ? DataSourceIcons[dataSource] || DataSourceIcons.payhelm : null
  const sourceName = dataSource ? getAgentDataSourceDisplayName(dataSource) : null

  return (
    <div className={`tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-rounded-md ${colors.bg} tw-border ${colors.border} tw-mb-2`}>
      <div className="tw-flex tw-items-center tw-gap-2">
        {/* Data Source Icon */}
        {SourceIcon && (
          <div
            className="tw-w-5 tw-h-5 tw-rounded tw-bg-white tw-border tw-border-gray-200 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0"
            title={`Source: ${sourceName}`}
          >
            <SourceIcon size={12} />
          </div>
        )}
        <div className="tw-w-6 tw-h-6 tw-rounded-md tw-bg-white tw-shadow-sm tw-flex tw-items-center tw-justify-center">
          <Icon className={colors.icon} size={14} />
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <h3 className="tw-font-semibold tw-text-gray-800 tw-text-sm tw-truncate tw-max-w-[200px]" title={title}>{title}</h3>
          {subtitle && <span className="tw-text-xs tw-text-gray-500">· {subtitle}</span>}
          {sourceName && (
            <span className="tw-text-[10px] tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-white tw-text-gray-500 tw-font-medium tw-border tw-border-gray-200">
              {sourceName}
            </span>
          )}
        </div>
      </div>
      {action}
    </div>
  )
}

// Revenue Trend Chart
const RevenueTrendChart: React.FC<{ data: { date: string; value: number }[]; isExpanded: boolean }> = ({ data, isExpanded }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null)

  useEffect(() => {
    if (chartRef.current?.chart) {
      setTimeout(() => chartRef.current?.chart?.reflow(), 300)
    }
  }, [isExpanded])

  if (!data || data.length === 0) return null

  const categories = data.map(d => formatCalendarDate(d.date, { month: 'short', day: 'numeric' }))
  const values = data.map(d => d.value)

  const options: Highcharts.Options = {
    chart: {
      type: 'areaspline',
      backgroundColor: 'transparent',
      height: 160,
      style: { fontFamily: 'inherit' },
      spacing: [10, 10, 10, 10],
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      categories,
      labels: {
        style: { fontSize: '10px', color: '#9ca3af' },
        rotation: categories.length > 10 ? -45 : 0,
      },
      lineColor: '#e5e7eb',
      tickColor: '#e5e7eb',
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: '#f3f4f6',
      labels: {
        style: { fontSize: '10px', color: '#9ca3af' },
        formatter: function() { return '$' + (Number(this.value) / 1000).toFixed(0) + 'K' },
      },
    },
    plotOptions: {
      areaspline: {
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [[0, 'rgba(59, 130, 246, 0.3)'], [1, 'rgba(59, 130, 246, 0.05)']],
        },
        lineWidth: 2,
        lineColor: '#3b82f6',
        marker: { radius: 3, fillColor: '#3b82f6' },
      },
    },
    tooltip: {
      backgroundColor: '#1f2937',
      borderColor: '#374151',
      borderRadius: 8,
      style: { color: '#f9fafb', fontSize: '11px' },
      formatter: function() { return `<b>${this.x}</b><br/>Revenue: $${Number(this.y).toLocaleString()}` },
    },
    series: [{ type: 'areaspline', name: 'Revenue', data: values }],
  }

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-3 tw-shadow-sm">
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
        <BiLineChart className="tw-text-blue-600" size={14} />
        <span className="tw-font-medium tw-text-gray-700 tw-text-xs">Revenue Trend</span>
      </div>
      <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
    </div>
  )
}

// Top Products Table - Compact
const TopProductsTable: React.FC<{ products: any[] }> = ({ products }) => {
  if (!products || products.length === 0) return null

  // Check if we have any actual revenue data
  const hasRevenueData = products.some(p => p.revenue > 0)
  const hasQuantityData = products.some(p => p.quantity > 0)

  // If no numeric data at all, show a simplified list
  if (!hasRevenueData && !hasQuantityData) {
    return (
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-border-b tw-border-gray-100">
          <FiPackage className="tw-text-amber-600" size={14} />
          <span className="tw-font-medium tw-text-gray-700 tw-text-xs">Top {products.length} Products</span>
        </div>
        <div className="tw-px-3 tw-py-2 tw-text-xs tw-text-gray-500 tw-italic">
          Products found (revenue data not available in search results)
        </div>
        <div className="tw-overflow-x-auto tw-max-h-[180px]">
          <ul className="tw-divide-y tw-divide-gray-50">
            {products.slice(0, 10).map((product, idx) => (
              <li key={idx} className="tw-px-3 tw-py-1.5 tw-flex tw-items-center tw-gap-2 hover:tw-bg-blue-50/50">
                <span className="tw-text-xs tw-text-gray-400 tw-w-4">{idx + 1}.</span>
                <span className="tw-text-xs tw-text-gray-800 tw-truncate" title={product.name}>
                  {product.name}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    )
  }

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-border-b tw-border-gray-100">
        <FiPackage className="tw-text-amber-600" size={14} />
        <span className="tw-font-medium tw-text-gray-700 tw-text-xs">Top Products</span>
      </div>
      <div className="tw-overflow-x-auto tw-max-h-[200px]">
        <table className="tw-w-full tw-text-xs">
          <thead className="tw-sticky tw-top-0">
            <tr className="tw-bg-gray-50">
              <th className="tw-px-2 tw-py-1.5 tw-text-left tw-font-semibold tw-text-gray-600">#</th>
              <th className="tw-px-2 tw-py-1.5 tw-text-left tw-font-semibold tw-text-gray-600">Product</th>
              {hasRevenueData && (
                <th className="tw-px-2 tw-py-1.5 tw-text-right tw-font-semibold tw-text-gray-600">Revenue</th>
              )}
              {hasQuantityData && (
                <th className="tw-px-2 tw-py-1.5 tw-text-right tw-font-semibold tw-text-gray-600">Qty</th>
              )}
            </tr>
          </thead>
          <tbody>
            {products.slice(0, 10).map((product, idx) => (
              <tr key={idx} className="tw-border-b tw-border-gray-50 hover:tw-bg-blue-50/50">
                <td className="tw-px-2 tw-py-1.5 tw-text-gray-500">{idx + 1}</td>
                <td className="tw-px-2 tw-py-1.5 tw-text-gray-800 tw-font-medium tw-max-w-[150px] tw-truncate" title={product.name}>
                  {product.name}
                </td>
                {hasRevenueData && (
                  <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-green-600 tw-font-semibold">
                    {formatCurrency(product.revenue)}
                  </td>
                )}
                {hasQuantityData && (
                  <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-600">
                    {formatNumber(product.quantity)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Category Breakdown Chart
const CategoryBreakdownChart: React.FC<{ data: { name: string; value: number }[]; isExpanded: boolean }> = ({ data, isExpanded }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null)

  useEffect(() => {
    if (chartRef.current?.chart) {
      setTimeout(() => chartRef.current?.chart?.reflow(), 300)
    }
  }, [isExpanded])

  if (!data || data.length === 0) return null

  const options: Highcharts.Options = {
    chart: {
      type: 'pie',
      backgroundColor: 'transparent',
      height: 160,
      style: { fontFamily: 'inherit' },
      spacing: [5, 5, 5, 5],
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: {
      enabled: true,
      itemStyle: { fontSize: '10px', fontWeight: '400', color: '#6b7280' },
    },
    plotOptions: {
      pie: {
        innerSize: '50%',
        dataLabels: {
          enabled: true,
          format: '{point.percentage:.1f}%',
          style: { fontSize: '9px', color: '#6b7280' },
          distance: -20,
        },
        colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'],
      },
    },
    tooltip: {
      backgroundColor: '#1f2937',
      borderColor: '#374151',
      borderRadius: 8,
      style: { color: '#f9fafb', fontSize: '11px' },
      formatter: function() { return `<b>${this.point.name}</b><br/>$${Number(this.y).toLocaleString()}` },
    },
    series: [{
      type: 'pie',
      name: 'Revenue',
      data: data.map(d => ({ name: d.name, y: d.value })),
    }],
  }

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-3 tw-shadow-sm">
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
        <BiPieChartAlt2 className="tw-text-purple-600" size={14} />
        <span className="tw-font-medium tw-text-gray-700 tw-text-xs">Revenue by Category</span>
      </div>
      <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
    </div>
  )
}

// Products Bar Chart - Compact
const ProductsBarChart: React.FC<{ products: any[]; isExpanded: boolean }> = ({ products, isExpanded }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null)

  useEffect(() => {
    if (chartRef.current?.chart) {
      setTimeout(() => chartRef.current?.chart?.reflow(), 300)
    }
  }, [isExpanded])

  if (!products || products.length === 0) return null

  // Check if we have any actual revenue data
  const hasRevenueData = products.some(p => p.revenue > 0)

  // Don't show chart if no revenue data
  if (!hasRevenueData) {
    return null
  }

  const top5 = products.slice(0, 5)
  const categories = top5.map(p => p.name.length > 15 ? p.name.slice(0, 12) + '...' : p.name)
  const values = top5.map(p => p.revenue)

  const options: Highcharts.Options = {
    chart: {
      type: 'bar',
      backgroundColor: 'transparent',
      height: 160,
      style: { fontFamily: 'inherit' },
      spacing: [5, 10, 5, 5],
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      categories,
      labels: { style: { fontSize: '10px', color: '#6b7280' } },
      lineColor: '#e5e7eb',
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: '#f3f4f6',
      labels: {
        style: { fontSize: '10px', color: '#9ca3af' },
        formatter: function() { return '$' + (Number(this.value) / 1000).toFixed(0) + 'K' },
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        color: '#10b981',
        dataLabels: {
          enabled: true,
          formatter: function() { return '$' + (Number(this.y) / 1000).toFixed(1) + 'K' },
          style: { fontSize: '9px', fontWeight: '400', color: '#6b7280' },
        },
      },
    },
    tooltip: {
      backgroundColor: '#1f2937',
      borderColor: '#374151',
      borderRadius: 8,
      style: { color: '#f9fafb', fontSize: '11px' },
    },
    series: [{ type: 'bar', name: 'Revenue', data: values }],
  }

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-3 tw-shadow-sm">
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
        <BiBarChartAlt2 className="tw-text-green-600" size={14} />
        <span className="tw-font-medium tw-text-gray-700 tw-text-xs">Top 5 Products</span>
      </div>
      <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
    </div>
  )
}

// Period Comparison Chart - Grouped bar chart for year-over-year comparisons
const PeriodComparisonChart: React.FC<{ data: PeriodComparison; isExpanded: boolean }> = ({ data, isExpanded }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null)

  useEffect(() => {
    if (chartRef.current?.chart) {
      setTimeout(() => chartRef.current?.chart?.reflow(), 300)
    }
  }, [isExpanded])

  if (!data || data.metrics.length === 0) return null

  // Filter to show currency/number metrics that are good for bar chart comparison
  const chartableMetrics = data.metrics.filter(m =>
    m.type !== 'percent' && m.values.some(v => v.value > 0)
  ).slice(0, 8)

  if (chartableMetrics.length === 0) return null

  const categories = chartableMetrics.map(m =>
    m.name.length > 20 ? m.name.slice(0, 18) + '...' : m.name
  )

  // Create series for each period
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']
  const series: Highcharts.SeriesColumnOptions[] = data.periods.map((period, idx) => ({
    type: 'column',
    name: period,
    data: chartableMetrics.map(m => m.values.find(v => v.period === period)?.value || 0),
    color: colors[idx % colors.length],
  }))

  const options: Highcharts.Options = {
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      height: 200,
      style: { fontFamily: 'inherit' },
      spacing: [10, 10, 10, 10],
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: {
      enabled: true,
      itemStyle: { fontSize: '11px', fontWeight: '500', color: '#4b5563' },
      symbolHeight: 8,
      symbolWidth: 16,
      symbolRadius: 2,
    },
    xAxis: {
      categories,
      labels: {
        style: { fontSize: '10px', color: '#6b7280' },
        rotation: categories.length > 5 ? -30 : 0,
      },
      lineColor: '#e5e7eb',
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: '#f3f4f6',
      labels: {
        style: { fontSize: '10px', color: '#9ca3af' },
        formatter: function() {
          const val = Number(this.value)
          if (val >= 1000000) return '$' + (val / 1000000).toFixed(1) + 'M'
          if (val >= 1000) return '$' + (val / 1000).toFixed(0) + 'K'
          return '$' + val.toLocaleString()
        },
      },
    },
    plotOptions: {
      column: {
        borderRadius: 3,
        groupPadding: 0.15,
        pointPadding: 0.05,
      },
    },
    tooltip: {
      backgroundColor: '#1f2937',
      borderColor: '#374151',
      borderRadius: 8,
      style: { color: '#f9fafb', fontSize: '11px' },
      formatter: function() {
        const val = Number(this.y)
        const formatted = val >= 1000000 ? '$' + (val / 1000000).toFixed(2) + 'M' :
                         val >= 1000 ? '$' + (val / 1000).toFixed(1) + 'K' :
                         '$' + val.toLocaleString()
        return `<b>${this.x}</b><br/>${this.series.name}: ${formatted}`
      },
    },
    series,
  }

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-shadow-sm">
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
        <BiBarChartAlt2 className="tw-text-blue-600" size={16} />
        <span className="tw-font-semibold tw-text-gray-800 tw-text-sm">Period Comparison</span>
        {data.periodStart && data.periodEnd && (
          <span className="tw-text-xs tw-text-gray-500">
            {data.periodStart} - {data.periodEnd}
          </span>
        )}
      </div>
      <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
    </div>
  )
}

// Period Comparison Table - Shows metrics with period columns like PayHelm reports
const PeriodComparisonTable: React.FC<{ data: PeriodComparison }> = ({ data }) => {
  if (!data || data.metrics.length === 0) return null

  const formatValue = (value: number, type: 'currency' | 'number' | 'percent'): string => {
    if (type === 'currency') {
      if (value >= 1000000) return '$' + (value / 1000000).toFixed(2) + 'M'
      if (value >= 1000) return '$' + (value / 1000).toFixed(2) + 'K'
      return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
    if (type === 'percent') {
      return value.toFixed(1) + '%'
    }
    if (value >= 1000000) return (value / 1000000).toFixed(2) + 'M'
    if (value >= 1000) return (value / 1000).toFixed(2) + 'K'
    return value.toLocaleString()
  }

  // Calculate change between periods if we have exactly 2 periods
  const calculateChange = (metric: typeof data.metrics[0]): { value: number; direction: 'up' | 'down' | 'neutral' } | null => {
    if (data.periods.length !== 2) return null
    const oldVal = metric.values.find(v => v.period === data.periods[0])?.value || 0
    const newVal = metric.values.find(v => v.period === data.periods[1])?.value || 0
    if (oldVal === 0) return null
    const change = ((newVal - oldVal) / oldVal) * 100
    return {
      value: Math.abs(change),
      direction: change > 0.5 ? 'up' : change < -0.5 ? 'down' : 'neutral'
    }
  }

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-border-b tw-border-gray-100 tw-bg-gray-50">
        <FiBarChart2 className="tw-text-indigo-600" size={16} />
        <span className="tw-font-semibold tw-text-gray-800 tw-text-sm">Metrics Comparison</span>
      </div>
      <div className="tw-overflow-x-auto">
        <table className="tw-w-full tw-text-sm">
          <thead>
            <tr className="tw-bg-gray-50/80">
              <th className="tw-px-4 tw-py-2.5 tw-text-left tw-font-semibold tw-text-gray-700 tw-border-b tw-border-gray-200">
                Metric
              </th>
              {data.periods.map((period, idx) => (
                <th
                  key={idx}
                  className="tw-px-4 tw-py-2.5 tw-text-right tw-font-semibold tw-text-gray-700 tw-border-b tw-border-gray-200"
                >
                  {period}
                </th>
              ))}
              {data.periods.length === 2 && (
                <th className="tw-px-4 tw-py-2.5 tw-text-right tw-font-semibold tw-text-gray-700 tw-border-b tw-border-gray-200">
                  Change
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.metrics.map((metric, idx) => {
              const change = calculateChange(metric)
              return (
                <tr
                  key={idx}
                  className={`tw-border-b tw-border-gray-50 hover:tw-bg-blue-50/30 ${
                    idx % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50/30'
                  }`}
                >
                  <td className="tw-px-4 tw-py-2.5 tw-text-gray-800 tw-font-medium">
                    {metric.name}
                  </td>
                  {data.periods.map((period, pidx) => {
                    const val = metric.values.find(v => v.period === period)?.value || 0
                    return (
                      <td
                        key={pidx}
                        className={`tw-px-4 tw-py-2.5 tw-text-right tw-font-medium ${
                          metric.type === 'currency' ? 'tw-text-green-600' : 'tw-text-gray-700'
                        }`}
                      >
                        {formatValue(val, metric.type)}
                      </td>
                    )
                  })}
                  {data.periods.length === 2 && (
                    <td className="tw-px-4 tw-py-2.5 tw-text-right">
                      {change && (
                        <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-font-medium ${
                          change.direction === 'up' ? 'tw-text-green-600' :
                          change.direction === 'down' ? 'tw-text-red-600' : 'tw-text-gray-500'
                        }`}>
                          {change.direction === 'up' && <BiTrendingUp size={14} />}
                          {change.direction === 'down' && <BiTrendingDown size={14} />}
                          {change.value.toFixed(1)}%
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Dynamic Chart - auto-generated based on detected data structure
const DynamicChart: React.FC<{ chart: DetectedChart; isExpanded: boolean; query: string }> = ({ chart, isExpanded, query }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null)

  useEffect(() => {
    if (chartRef.current?.chart) {
      setTimeout(() => chartRef.current?.chart?.reflow(), 300)
    }
  }, [isExpanded])

  if (!chart || chart.values.length === 0) return null

  const formatValue = (val: number): string => {
    if (chart.valueField.toLowerCase().includes('revenue') ||
        chart.valueField.toLowerCase().includes('amount') ||
        chart.valueField.toLowerCase().includes('total') ||
        chart.valueField.toLowerCase().includes('sales')) {
      if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`
      if (val >= 1000) return `$${(val / 1000).toFixed(1)}K`
      return `$${val.toFixed(2)}`
    }
    if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`
    if (val >= 1000) return `${(val / 1000).toFixed(1)}K`
    return val.toLocaleString()
  }

  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#84cc16', '#14b8a6']

  let options: Highcharts.Options

  if (chart.type === 'line' || chart.type === 'area') {
    options = {
      chart: {
        type: chart.type === 'area' ? 'areaspline' : 'spline',
        backgroundColor: 'transparent',
        height: 140,
        style: { fontFamily: 'inherit' },
        spacing: [5, 5, 5, 5],
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        categories: chart.categories.map(c => formatDateForChart(c)),
        labels: { style: { fontSize: '9px', color: '#9ca3af' }, rotation: chart.categories.length > 8 ? -45 : 0 },
        lineColor: '#e5e7eb',
      },
      yAxis: {
        title: { text: undefined },
        gridLineColor: '#f3f4f6',
        labels: { style: { fontSize: '9px', color: '#9ca3af' }, formatter: function() { return formatValue(Number(this.value)) } },
      },
      plotOptions: {
        areaspline: {
          fillColor: { linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, stops: [[0, 'rgba(59, 130, 246, 0.3)'], [1, 'rgba(59, 130, 246, 0.05)']] },
          lineWidth: 2,
          lineColor: '#3b82f6',
          marker: { radius: 2, fillColor: '#3b82f6' },
        },
        spline: { lineWidth: 2, lineColor: '#3b82f6', marker: { radius: 2, fillColor: '#3b82f6' } },
      },
      tooltip: {
        backgroundColor: '#1f2937',
        borderColor: '#374151',
        borderRadius: 6,
        style: { color: '#f9fafb', fontSize: '10px' },
        formatter: function() { return `<b>${this.x}</b><br/>${formatValue(Number(this.y))}` },
      },
      series: [{ type: chart.type === 'area' ? 'areaspline' : 'spline', name: chart.valueField, data: chart.values }],
    }
  } else if (chart.type === 'bar') {
    options = {
      chart: {
        type: 'bar',
        backgroundColor: 'transparent',
        height: 140,
        style: { fontFamily: 'inherit' },
        spacing: [5, 5, 5, 5],
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        categories: chart.categories.map(c => c.length > 12 ? c.slice(0, 10) + '...' : c),
        labels: { style: { fontSize: '9px', color: '#6b7280' } },
        lineColor: '#e5e7eb',
      },
      yAxis: {
        title: { text: undefined },
        gridLineColor: '#f3f4f6',
        labels: { style: { fontSize: '9px', color: '#9ca3af' }, formatter: function() { return formatValue(Number(this.value)) } },
      },
      plotOptions: {
        bar: {
          borderRadius: 3,
          color: '#10b981',
          dataLabels: { enabled: chart.values.length <= 5, formatter: function() { return formatValue(Number(this.y)) }, style: { fontSize: '8px', fontWeight: '400', color: '#6b7280' } },
        },
      },
      tooltip: {
        backgroundColor: '#1f2937',
        borderColor: '#374151',
        borderRadius: 6,
        style: { color: '#f9fafb', fontSize: '10px' },
      },
      series: [{ type: 'bar', name: chart.valueField, data: chart.values }],
    }
  } else {
    // Pie chart
    options = {
      chart: {
        type: 'pie',
        backgroundColor: 'transparent',
        height: 140,
        style: { fontFamily: 'inherit' },
        spacing: [2, 2, 2, 2],
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      plotOptions: {
        pie: {
          innerSize: '45%',
          dataLabels: { enabled: true, format: '{point.percentage:.0f}%', style: { fontSize: '8px', color: '#6b7280' }, distance: -15 },
          colors,
        },
      },
      tooltip: {
        backgroundColor: '#1f2937',
        borderColor: '#374151',
        borderRadius: 6,
        style: { color: '#f9fafb', fontSize: '10px' },
        formatter: function() { return `<b>${this.point.name}</b><br/>${formatValue(Number(this.y))}` },
      },
      series: [{
        type: 'pie',
        name: chart.valueField,
        data: chart.categories.map((cat, i) => ({ name: cat, y: chart.values[i] })),
      }],
    }
  }

  const iconMap = { line: BiLineChart, area: BiLineChart, bar: BiBarChartAlt2, pie: BiPieChartAlt2 }
  const Icon = iconMap[chart.type] || BiBarChartAlt2

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-2 tw-shadow-sm">
      <div className="tw-flex tw-items-center tw-gap-1 tw-mb-1">
        <Icon className="tw-text-blue-600" size={12} />
        <span className="tw-font-medium tw-text-gray-700 tw-text-[10px] tw-truncate">{chart.title}</span>
      </div>
      <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
    </div>
  )
}

// Compact Data Table - for inline display with charts
const DataTable: React.FC<{ rows: any[]; maxRows?: number }> = ({ rows, maxRows = 8 }) => {
  if (!rows || rows.length === 0) return null

  // Filter out metadata keys
  const keys = Object.keys(rows[0])
    .filter(k => !EXCLUDE_FIELDS.some(ex => k.toLowerCase() === ex || k.toLowerCase().includes(ex)))
    .slice(0, 5) // Max 5 columns
  const displayRows = rows.slice(0, maxRows)

  const formatCell = (value: any, key: string): string => {
    if (value === null || value === undefined) return '-'

    // Handle objects - extract meaningful value
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      if (value.name !== undefined) return formatCell(value.name, key)
      if (value.value !== undefined) return formatCell(value.value, key)
      if (value.title !== undefined) return formatCell(value.title, key)
      if (value.label !== undefined) return formatCell(value.label, key)
      const objKeys = Object.keys(value)
      if (objKeys.length === 0) return '-'
      if (objKeys.length <= 2) return objKeys.map(k => `${k}: ${value[k]}`).join(', ')
      return `${objKeys.length} fields`
    }

    // Handle arrays
    if (Array.isArray(value)) {
      if (value.length === 0) return '-'
      if (typeof value[0] === 'string' || typeof value[0] === 'number') {
        return value.slice(0, 2).join(', ') + (value.length > 2 ? '...' : '')
      }
      return `${value.length} items`
    }

    if (typeof value === 'number') {
      if (key.toLowerCase().includes('revenue') || key.toLowerCase().includes('amount') || key.toLowerCase().includes('total') || key.toLowerCase().includes('sales') || key.toLowerCase().includes('price')) {
        return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      }
      if (key.toLowerCase().includes('percent') || key.toLowerCase().includes('rate')) {
        return `${value.toFixed(1)}%`
      }
      return value.toLocaleString()
    }
    if (typeof value === 'string' && value.length > 25) {
      return value.slice(0, 22) + '...'
    }
    return String(value)
  }

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
      <div className="tw-overflow-x-auto tw-max-h-[180px]">
        <table className="tw-w-full tw-text-[10px]">
          <thead className="tw-sticky tw-top-0">
            <tr className="tw-bg-gray-50">
              {keys.map((key, i) => (
                <th key={i} className="tw-px-2 tw-py-1 tw-text-left tw-font-semibold tw-text-gray-600 tw-whitespace-nowrap">
                  {key.replace(/_/g, ' ').slice(0, 15)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, rowIdx) => (
              <tr key={rowIdx} className="tw-border-b tw-border-gray-50 hover:tw-bg-blue-50/30">
                {keys.map((key, colIdx) => {
                  const cellValue = row[key]
                  const titleText = typeof cellValue === 'string' ? cellValue : formatCell(cellValue, key)
                  return (
                    <td
                      key={colIdx}
                      className={`tw-px-2 tw-py-1 tw-whitespace-nowrap ${
                        typeof cellValue === 'number' &&
                        (key.toLowerCase().includes('revenue') || key.toLowerCase().includes('total') || key.toLowerCase().includes('amount'))
                          ? 'tw-text-green-600 tw-font-medium'
                          : 'tw-text-gray-700'
                      }`}
                      title={titleText}
                    >
                      {formatCell(cellValue, key)}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > maxRows && (
        <div className="tw-px-2 tw-py-1 tw-text-[9px] tw-text-gray-400 tw-bg-gray-50 tw-text-center">
          +{rows.length - maxRows} more rows
        </div>
      )}
    </div>
  )
}

// Currency fields for formatting detection
const CURRENCY_FIELDS = ['revenue', 'sales', 'total', 'amount', 'price', 'profit', 'cost', 'tax', 'shipping', 'discount', 'gross', 'net', 'avg_order', 'avg_price', 'subtotal', 'refund', 'gross_sales', 'net_sales']
const PERCENT_FIELDS = ['margin', 'rate', 'percent', 'percentage', 'ratio', 'conversion', 'bounce_rate']
const COUNT_FIELDS = ['count', 'orders', 'items', 'quantity', 'customers', 'units', 'qty', 'pageviews', 'total_pageviews', 'sessions', 'visitors', 'unique_visitors', 'items_sold', 'stock', 'inventory']
const IMAGE_FIELDS = ['image', 'image_url', 'img', 'thumbnail', 'photo', 'picture']

// Report-Style Table - Full table like PayHelm Reports page
const ReportStyleTable: React.FC<{
  rows: any[]
  query: string
  maxRows?: number
  reportUrl?: string
}> = ({ rows, query, maxRows = 20, reportUrl }) => {
  if (!rows || rows.length === 0) return null

  // Get keys and filter out metadata fields
  const allKeys = Object.keys(rows[0]).filter(key => {
    const lower = key.toLowerCase()
    // Exclude metadata-like fields
    return !EXCLUDE_FIELDS.some(ex => lower === ex || lower.includes(ex))
  })


  // If no keys left after filtering, show nothing
  if (allKeys.length === 0) return null

  // Prioritize important columns first - includes sales, traffic, and inventory fields
  const priorityOrder = [
    // Identity fields
    'name', 'product_name', 'title', 'sku', 'customer', 'email', 'page', 'url', 'path',
    // Date fields
    'date', 'order_date', 'date_trunc',
    // Status
    'status',
    // Sales metrics
    'total', 'revenue', 'gross_sales', 'net_sales', 'sales', 'amount', 'quantity', 'orders', 'items_sold', 'profit', 'margin', 'tax', 'shipping', 'discount',
    // Traffic metrics
    'pageviews', 'total_pageviews', 'sessions', 'visitors', 'unique_visitors', 'bounce_rate',
    // Inventory metrics
    'stock', 'inventory', 'available',
    // Other
    'count', 'price', 'category', 'type', 'description'
  ]

  // Sort keys by priority
  const sortedKeys = [...allKeys].sort((a, b) => {
    const aLower = a.toLowerCase()
    const bLower = b.toLowerCase()
    const aIdx = priorityOrder.findIndex(p => aLower.includes(p))
    const bIdx = priorityOrder.findIndex(p => bLower.includes(p))
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx
    if (aIdx !== -1) return -1
    if (bIdx !== -1) return 1
    return 0
  })

  // Take top 10 columns for display
  const displayKeys = sortedKeys.slice(0, 10)
  const displayRows = rows.slice(0, maxRows)

  // Format column header
  const formatHeader = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim()
  }

  // Detect if field is currency
  const isCurrencyField = (key: string): boolean => {
    const lower = key.toLowerCase()
    return CURRENCY_FIELDS.some(f => lower.includes(f))
  }

  // Detect if field is percentage
  const isPercentField = (key: string): boolean => {
    const lower = key.toLowerCase()
    return PERCENT_FIELDS.some(f => lower.includes(f))
  }

  // Detect if field is a count
  const isCountField = (key: string): boolean => {
    const lower = key.toLowerCase()
    return COUNT_FIELDS.some(f => lower.includes(f))
  }

  // Detect if field is an image
  const isImageField = (key: string): boolean => {
    const lower = key.toLowerCase()
    return IMAGE_FIELDS.some(f => lower.includes(f))
  }

  // Detect if field should be right-aligned (numeric)
  const isNumericField = (key: string, sampleValue: any): boolean => {
    return isCurrencyField(key) || isPercentField(key) || isCountField(key) || typeof sampleValue === 'number'
  }

  // Format cell value
  const formatCellValue = (value: any, key: string): React.ReactNode => {
    if (value === null || value === undefined || value === '') return <span className="tw-text-gray-400">-</span>

    // Handle objects - extract meaningful value or stringify
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Try to extract common fields
      if (value.name !== undefined) return formatCellValue(value.name, key)
      if (value.value !== undefined) return formatCellValue(value.value, key)
      if (value.title !== undefined) return formatCellValue(value.title, key)
      if (value.label !== undefined) return formatCellValue(value.label, key)
      if (value.text !== undefined) return formatCellValue(value.text, key)
      if (value.id !== undefined) return formatCellValue(value.id, key)

      // For other objects, try to show a meaningful summary
      const keys = Object.keys(value)
      if (keys.length === 0) return <span className="tw-text-gray-400">-</span>
      if (keys.length <= 2) {
        // Small object - show key:value pairs
        return keys.map(k => `${k}: ${value[k]}`).join(', ')
      }
      // Larger object - just show key count
      return <span className="tw-text-gray-500 tw-italic">{keys.length} fields</span>
    }

    // Handle arrays
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="tw-text-gray-400">-</span>
      if (typeof value[0] === 'string' || typeof value[0] === 'number') {
        const display = value.slice(0, 3).join(', ')
        return value.length > 3 ? `${display}...` : display
      }
      return <span className="tw-text-gray-500 tw-italic">{value.length} items</span>
    }

    // Image fields
    if (isImageField(key) && typeof value === 'string' && value.startsWith('http')) {
      return (
        <img
          src={value}
          alt="Product"
          className="tw-w-10 tw-h-10 tw-object-cover tw-rounded tw-border tw-border-gray-200"
          onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/image_placeholder.png' }}
        />
      )
    }

    // Currency fields
    if (isCurrencyField(key) && typeof value === 'number') {
      if (value >= 1000000) return <span className="tw-text-green-600 tw-font-semibold">${(value / 1000000).toFixed(2)}M</span>
      if (value >= 1000) return <span className="tw-text-green-600 tw-font-semibold">${(value / 1000).toFixed(2)}K</span>
      return <span className="tw-text-green-600 tw-font-semibold">${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
    }

    // Percentage fields
    if (isPercentField(key) && typeof value === 'number') {
      const color = value > 0 ? 'tw-text-green-600' : value < 0 ? 'tw-text-red-600' : 'tw-text-gray-700'
      return <span className={`tw-font-medium ${color}`}>{value.toFixed(1)}%</span>
    }

    // Count/number fields
    if ((isCountField(key) || typeof value === 'number') && typeof value === 'number') {
      if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`
      if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
      return value.toLocaleString()
    }

    // Date fields
    if (key.toLowerCase().includes('date') && typeof value === 'string') {
      try {
        const formatted = formatCalendarDate(value, { month: 'short', day: 'numeric', year: 'numeric' })
        if (formatted) return formatted
      } catch {}
    }

    // String truncation
    if (typeof value === 'string' && value.length > 40) {
      return value.slice(0, 37) + '...'
    }

    return String(value)
  }

  return (
    <div className="tw-bg-white tw-rounded-xl tw-border-2 tw-border-blue-200 tw-shadow-md tw-overflow-hidden">
      {/* Table Header - More prominent with blue gradient */}
      <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-bg-gradient-to-r tw-from-blue-100 tw-to-blue-50 tw-border-b-2 tw-border-blue-200">
        <div className="tw-flex tw-items-center tw-gap-3">
          <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-blue-600 tw-flex tw-items-center tw-justify-center tw-shadow-sm">
            <FiBarChart2 className="tw-text-white" size={18} />
          </div>
          <div>
            <h4 className="tw-font-bold tw-text-gray-900 tw-text-base">{query}</h4>
            <p className="tw-text-sm tw-text-blue-700 tw-font-medium">{rows.length} records</p>
          </div>
        </div>
        {reportUrl && (
          <a
            href={reportUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-semibold hover:tw-bg-blue-700 tw-transition-colors tw-shadow-md"
          >
            <BiLinkExternal size={14} />
            View Full Report
          </a>
        )}
      </div>

      {/* Table */}
      <div className="tw-overflow-x-auto">
        <table className="tw-w-full tw-text-sm">
          <thead>
            <tr className="tw-bg-gray-200">
              {displayKeys.map((key, idx) => (
                <th
                  key={idx}
                  className={`tw-px-4 tw-py-3 tw-font-bold tw-text-gray-800 tw-text-xs tw-uppercase tw-tracking-wider tw-whitespace-nowrap tw-border-b-2 tw-border-gray-300 ${
                    isImageField(key) ? 'tw-text-center tw-w-16' :
                    isNumericField(key, rows[0]?.[key]) ? 'tw-text-right' : 'tw-text-left'
                  }`}
                >
                  {isImageField(key) ? '' : formatHeader(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className={`tw-transition-colors hover:tw-bg-blue-100 ${rowIdx % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-100'}`}
              >
                {displayKeys.map((key, colIdx) => {
                  const isNumeric = isNumericField(key, row[key])
                  const isImg = isImageField(key)
                  return (
                    <td
                      key={colIdx}
                      className={`tw-px-4 tw-py-3 tw-whitespace-nowrap tw-border-b tw-border-gray-200 ${
                        isImg ? 'tw-text-center tw-w-16' :
                        isNumeric ? 'tw-text-right' : 'tw-text-left tw-max-w-xs tw-truncate'
                      }`}
                      title={typeof row[key] === 'string' ? row[key] : undefined}
                    >
                      {formatCellValue(row[key], key)}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Table Footer */}
      {rows.length > maxRows && (
        <div className="tw-px-4 tw-py-3 tw-bg-blue-50 tw-border-t-2 tw-border-blue-200 tw-text-center">
          <span className="tw-text-sm tw-text-blue-700 tw-font-medium">
            Showing {maxRows} of {rows.length} records
          </span>
        </div>
      )}
    </div>
  )
}

// Query Result Card - combines chart + table for a single query result
const QueryResultCard: React.FC<{
  result: { query: string; data: any[]; reportUrl?: string }
  isExpanded: boolean
}> = ({ result, isExpanded }) => {
  if (!result.data || result.data.length === 0) return null

  // First check for period comparison data
  const periodComparison = detectPeriodComparison(result.data)

  // If this is period comparison data, show specialized view
  if (periodComparison && periodComparison.metrics.length > 0) {
    return (
      <div className="tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200 tw-p-3">
        {/* Query Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-3">
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-w-6 tw-h-6 tw-rounded tw-bg-indigo-100 tw-flex tw-items-center tw-justify-center">
              <BiCalendar className="tw-text-indigo-600" size={14} />
            </div>
            <span className="tw-text-sm tw-font-semibold tw-text-gray-800">
              {result.query}
            </span>
          </div>
          {result.reportUrl && (
            <a
              href={result.reportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-bg-blue-600 tw-text-white tw-rounded-md tw-text-xs tw-font-medium hover:tw-bg-blue-700 tw-flex-shrink-0"
            >
              <BiLinkExternal size={12} />
              View Report
            </a>
          )}
        </div>

        {/* Period Comparison Chart and Table */}
        <div className="tw-space-y-3">
          <PeriodComparisonChart data={periodComparison} isExpanded={isExpanded} />
          <PeriodComparisonTable data={periodComparison} />
        </div>
      </div>
    )
  }

  // Regular data - use existing logic
  // Normalize MCP data for better chart detection
  const normalizedData = normalizeMcpData(result.data)

  const structure = analyzeDataStructure(normalizedData, result.query)
  const chart = detectChartType(normalizedData, structure, result.query)
  const metrics = extractMetricsFromData(normalizedData, result.query)

  return (
    <div className="tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200 tw-p-2">
      {/* Query Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-2">
        <span className="tw-text-xs tw-font-medium tw-text-gray-700 tw-truncate tw-flex-1" title={result.query}>
          {result.query}
        </span>
        {result.reportUrl && (
          <a
            href={result.reportUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-bg-blue-600 tw-text-white tw-rounded tw-text-[10px] tw-font-medium hover:tw-bg-blue-700 tw-flex-shrink-0"
          >
            <BiLinkExternal size={10} />
            Report
          </a>
        )}
      </div>

      {/* Metrics Row */}
      {metrics.length > 0 && (
        <div className="tw-grid tw-grid-cols-2 tw-gap-1.5 tw-mb-2">
          {metrics.slice(0, 4).map((m, i) => (
            <div key={i} className="tw-bg-white tw-rounded tw-px-2 tw-py-1 tw-border tw-border-gray-100">
              <div className="tw-text-xs tw-font-bold tw-text-gray-900">
                {m.type === 'currency' ? formatCurrency(m.value) : formatNumber(m.value)}
              </div>
              <div className="tw-text-[9px] tw-text-gray-500 tw-truncate">{m.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Chart + Table */}
      <div className={`tw-grid ${chart ? 'tw-grid-cols-1 lg:tw-grid-cols-2' : 'tw-grid-cols-1'} tw-gap-2`}>
        {chart && <DynamicChart chart={chart} isExpanded={isExpanded} query={result.query} />}
        <DataTable rows={result.data} maxRows={chart ? 6 : 10} />
      </div>
    </div>
  )
}

// Report Links Panel - Compact
const ReportLinksPanel: React.FC<{ urls: string[] }> = ({ urls }) => {
  if (!urls || urls.length === 0) return null

  return (
    <div className="tw-bg-gradient-to-r tw-from-blue-50 tw-to-indigo-50 tw-rounded-lg tw-border tw-border-blue-200 tw-p-2.5">
      <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <FiBarChart2 className="tw-text-blue-600" size={14} />
          <span className="tw-font-medium tw-text-gray-800 tw-text-xs">Reports:</span>
        </div>
        {urls.map((url, idx) => (
          <a
            key={idx}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-blue-600 tw-text-white tw-rounded-md tw-text-xs tw-font-medium hover:tw-bg-blue-700 tw-transition-colors tw-shadow-sm"
          >
            <BiLinkExternal size={12} />
            View Report {urls.length > 1 ? idx + 1 : ''}
          </a>
        ))}
      </div>
    </div>
  )
}

// View mode types for visualization toggle
type ViewMode = 'auto' | 'table' | 'bar' | 'line' | 'pie'

// Dashboard section types for customization
type SectionType = 'report-links' | 'period-comparisons' | 'executive-summary' | 'query-results'

// Section configuration
interface SectionConfig {
  id: SectionType
  label: string
  icon: React.ComponentType<any>
  defaultVisible: boolean
}

const DEFAULT_SECTIONS: SectionConfig[] = [
  { id: 'report-links', label: 'Report Links', icon: BiLinkExternal, defaultVisible: true },
  { id: 'period-comparisons', label: 'Period Comparisons', icon: BiCalendar, defaultVisible: true },
  { id: 'executive-summary', label: 'Executive Summary', icon: FiTrendingUp, defaultVisible: true },
  { id: 'query-results', label: 'Query Results', icon: BiBarChartAlt2, defaultVisible: true },
]

// Dashboard Customize Panel
const DashboardCustomizePanel: React.FC<{
  sectionOrder: SectionType[]
  sectionVisibility: Record<SectionType, boolean>
  onToggleVisibility: (section: SectionType) => void
  onMoveSection: (section: SectionType, direction: 'up' | 'down') => void
  onReset: () => void
  isOpen: boolean
  onClose: () => void
}> = ({ sectionOrder, sectionVisibility, onToggleVisibility, onMoveSection, onReset, isOpen, onClose }) => {
  if (!isOpen) return null

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-lg tw-p-4 tw-mb-4">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
        <div className="tw-flex tw-items-center tw-gap-2">
          <BiCog className="tw-text-gray-600" size={16} />
          <span className="tw-font-semibold tw-text-gray-800 tw-text-sm">Customize Dashboard</span>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <button
            type="button"
            onClick={onReset}
            className="tw-text-xs tw-text-gray-500 hover:tw-text-gray-700 tw-flex tw-items-center tw-gap-1"
            title="Reset to default"
          >
            <BiReset size={14} />
            Reset
          </button>
          <button
            type="button"
            onClick={onClose}
            className="tw-text-gray-400 hover:tw-text-gray-600 tw-text-lg tw-leading-none"
          >
            ×
          </button>
        </div>
      </div>
      <div className="tw-space-y-1">
        {sectionOrder.map((sectionId, idx) => {
          const config = DEFAULT_SECTIONS.find(s => s.id === sectionId)
          if (!config) return null
          const Icon = config.icon
          const isVisible = sectionVisibility[sectionId]
          const canMoveUp = idx > 0
          const canMoveDown = idx < sectionOrder.length - 1

          return (
            <div
              key={sectionId}
              className={`tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-rounded-md tw-border tw-transition-all ${
                isVisible
                  ? 'tw-bg-gray-50 tw-border-gray-200'
                  : 'tw-bg-gray-100 tw-border-gray-200 tw-opacity-60'
              }`}
            >
              <div className="tw-flex tw-items-center tw-gap-2">
                <Icon size={14} className={isVisible ? 'tw-text-gray-600' : 'tw-text-gray-400'} />
                <span className={`tw-text-sm ${isVisible ? 'tw-text-gray-700' : 'tw-text-gray-500'}`}>
                  {config.label}
                </span>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1">
                {/* Move up/down buttons */}
                <button
                  type="button"
                  onClick={() => onMoveSection(sectionId, 'up')}
                  disabled={!canMoveUp}
                  className={`tw-p-1 tw-rounded ${
                    canMoveUp
                      ? 'tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-200'
                      : 'tw-text-gray-300 tw-cursor-not-allowed'
                  }`}
                  title="Move up"
                >
                  <BiChevronUp size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => onMoveSection(sectionId, 'down')}
                  disabled={!canMoveDown}
                  className={`tw-p-1 tw-rounded ${
                    canMoveDown
                      ? 'tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-200'
                      : 'tw-text-gray-300 tw-cursor-not-allowed'
                  }`}
                  title="Move down"
                >
                  <BiChevronDown size={16} />
                </button>
                {/* Visibility toggle */}
                <button
                  type="button"
                  onClick={() => onToggleVisibility(sectionId)}
                  className={`tw-p-1 tw-rounded tw-ml-1 ${
                    isVisible
                      ? 'tw-text-blue-600 hover:tw-text-blue-700 hover:tw-bg-blue-50'
                      : 'tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-200'
                  }`}
                  title={isVisible ? 'Hide section' : 'Show section'}
                >
                  {isVisible ? <BiShow size={16} /> : <BiHide size={16} />}
                </button>
              </div>
            </div>
          )
        })}
      </div>
      <p className="tw-text-xs tw-text-gray-400 tw-mt-3">
        Use arrows to reorder sections. Click eye icon to show/hide.
      </p>
    </div>
  )
}

// Data source color configurations for card styling
const sourceCardColors: Record<string, { gradient: string; bg: string; text: string; border: string; dot: string }> = {
  google: { gradient: 'tw-bg-gradient-to-r tw-from-blue-500 tw-via-red-500 tw-to-yellow-500', bg: 'tw-bg-blue-50', text: 'tw-text-blue-700', border: 'tw-border-blue-200', dot: 'tw-bg-blue-500' },
  google_analytics: { gradient: 'tw-bg-gradient-to-r tw-from-amber-500 tw-to-orange-600', bg: 'tw-bg-amber-50', text: 'tw-text-amber-800', border: 'tw-border-amber-200', dot: 'tw-bg-amber-500' },
  google_tag_manager: { gradient: 'tw-bg-gradient-to-r tw-from-blue-600 tw-to-blue-800', bg: 'tw-bg-blue-50', text: 'tw-text-blue-800', border: 'tw-border-blue-200', dot: 'tw-bg-blue-600' },
  shopify: { gradient: 'tw-bg-gradient-to-r tw-from-green-500 tw-to-emerald-600', bg: 'tw-bg-green-50', text: 'tw-text-green-700', border: 'tw-border-green-200', dot: 'tw-bg-green-500' },
  klaviyo: { gradient: 'tw-bg-gradient-to-r tw-from-gray-700 tw-to-gray-900', bg: 'tw-bg-gray-100', text: 'tw-text-gray-700', border: 'tw-border-gray-300', dot: 'tw-bg-gray-600' },
  facebook: { gradient: 'tw-bg-gradient-to-r tw-from-blue-600 tw-to-blue-700', bg: 'tw-bg-blue-50', text: 'tw-text-blue-700', border: 'tw-border-blue-200', dot: 'tw-bg-blue-600' },
  meta: { gradient: 'tw-bg-gradient-to-r tw-from-blue-500 tw-to-indigo-600', bg: 'tw-bg-indigo-50', text: 'tw-text-indigo-700', border: 'tw-border-indigo-200', dot: 'tw-bg-indigo-500' },
  shipsurance: { gradient: 'tw-bg-gradient-to-r tw-from-blue-500 tw-to-cyan-600', bg: 'tw-bg-cyan-50', text: 'tw-text-cyan-700', border: 'tw-border-cyan-200', dot: 'tw-bg-cyan-500' },
  payhelm: { gradient: 'tw-bg-gradient-to-r tw-from-blue-500 tw-to-indigo-500', bg: 'tw-bg-blue-50', text: 'tw-text-blue-700', border: 'tw-border-blue-200', dot: 'tw-bg-blue-500' },
  database: { gradient: 'tw-bg-gradient-to-r tw-from-gray-400 tw-to-gray-500', bg: 'tw-bg-gray-50', text: 'tw-text-gray-600', border: 'tw-border-gray-200', dot: 'tw-bg-gray-400' },
}

// Data Source Card Component - Fancy card for grid layout
const DataSourceCard: React.FC<{
  result: {
    query: string
    data: any[]
    reportUrl?: string
    isPeriodComparison?: boolean
    isConfigurationData?: boolean
    tool?: string
    toolInput?: any
    apiError?: string
    klaviyoErrors?: Array<{ detail?: string; title?: string; code?: string }>
  }
  index: number
  normalizedData: any[]
  chart: DetectedChart | null
  viewMode: ViewMode
  defaultMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  isExpanded: boolean
}> = ({ result, index, normalizedData, chart, viewMode, defaultMode, onViewModeChange, isExpanded }) => {
  const [dataExpanded, setDataExpanded] = useState(false)

  const queryText = result.query || 'Data Query'
  const dataSource = detectAgentDataSource(result.tool, queryText)
  const colors = sourceCardColors[dataSource] || sourceCardColors.database
  const SourceIcon = DataSourceIcons[dataSource] || DataSourceIcons.database
  const sourceName = getAgentDataSourceDisplayName(dataSource)
  const toolName = result.tool ? result.tool.replace(/^mcp__payhelm__/, '') : 'query'
  const findings = extractKeyFindings(result.data, queryText)

  const hasChart = chart !== null
  const effectiveMode = viewMode === 'auto' ? defaultMode : viewMode
  const availableModes: ViewMode[] = ['table']
  if (hasChart) {
    availableModes.unshift('bar', 'line')
    if (normalizedData.length <= 10) availableModes.push('pie')
  }

  const getChartForMode = (mode: ViewMode): DetectedChart | null => {
    if (!chart || mode === 'table') return null
    return { ...chart, type: mode === 'pie' ? 'pie' : mode === 'line' ? 'line' : 'bar' }
  }

  const activeChart = getChartForMode(effectiveMode)
  const showChart = effectiveMode !== 'table' && !!activeChart
  const showTable = effectiveMode === 'table' || (effectiveMode !== 'table' && !activeChart)

  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm hover:tw-shadow-lg tw-transition-all tw-duration-200 tw-overflow-hidden tw-flex tw-flex-col">
      {/* Gradient top border */}
      <div className={`tw-h-1 ${colors.gradient}`} />

      {/* Card content */}
      <div className="tw-p-4 tw-flex tw-flex-col tw-flex-1">
        {/* Header: Source icon + name + status dot */}
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <div className="tw-flex tw-items-center tw-gap-2.5">
            <div className={`tw-w-10 tw-h-10 tw-rounded-lg ${colors.bg} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-border ${colors.border}`}>
              <SourceIcon size={22} />
            </div>
            <div>
              <span className={`tw-text-sm tw-font-bold ${colors.text}`}>{sourceName}</span>
              <div className="tw-flex tw-items-center tw-gap-1.5 tw-mt-0.5">
                <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-full ${result.apiError ? 'tw-bg-red-500' : colors.dot}`} />
                <span className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wider tw-font-medium">
                  {result.apiError ? 'Error' : 'Complete'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Query title */}
        <h4 className="tw-font-semibold tw-text-gray-800 tw-text-sm tw-leading-snug tw-mb-2" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {queryText}
        </h4>

        {/* Tool badge */}
        <div className="tw-mb-3">
          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-md tw-bg-gray-100 tw-text-gray-600 tw-text-[11px] tw-font-mono tw-border tw-border-gray-200">
            <BiWrench size={10} className="tw-text-gray-400" />
            {toolName}
          </span>
        </div>

        {result.apiError && (
          <div className="tw-mb-3 tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 tw-p-2.5">
            <div className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-red-600 tw-font-semibold tw-mb-1">
              Klaviyo API
            </div>
            <p className="tw-text-xs tw-text-red-800 tw-leading-snug">{result.apiError}</p>
            {result.klaviyoErrors && result.klaviyoErrors.length > 1 && (
              <ul className="tw-mt-1.5 tw-space-y-0.5 tw-list-disc tw-pl-4">
                {result.klaviyoErrors.slice(0, 4).map((err, i) => (
                  <li key={i} className="tw-text-[11px] tw-text-red-700">
                    {err.detail || err.title}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Key findings */}
        <div className="tw-flex-1 tw-mb-3">
          <div className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-gray-400 tw-font-semibold tw-mb-1.5">Key Findings</div>
          <ul className="tw-space-y-1">
            {(result.apiError && findings.length === 0
              ? ['Klaviyo data could not be loaded for this query.']
              : findings
            ).map((finding, i) => (
              <li key={i} className="tw-flex tw-items-start tw-gap-1.5 tw-text-xs tw-text-gray-600">
                <span className={`tw-w-1 tw-h-1 tw-rounded-full tw-mt-1.5 tw-flex-shrink-0 ${result.apiError ? 'tw-bg-red-400' : colors.dot}`} />
                {finding}
              </li>
            ))}
          </ul>
        </div>

        {/* Expandable data section */}
        <div className="tw-border-t tw-border-gray-100 tw-pt-3 tw-mt-auto">
          <button
            type="button"
            onClick={() => setDataExpanded(!dataExpanded)}
            className="tw-flex tw-items-center tw-justify-between tw-w-full tw-text-xs tw-font-medium tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors"
          >
            <span className="tw-flex tw-items-center tw-gap-1.5">
              {showChart ? <BiBarChartAlt2 size={13} /> : <BiTable size={13} />}
              {dataExpanded ? 'Hide Data' : 'Show Data'}
              <span className="tw-text-gray-400">({normalizedData.length} rows)</span>
            </span>
            {dataExpanded ? <BiChevronUp size={16} /> : <BiChevronDown size={16} />}
          </button>

          {dataExpanded && (
            <div className="tw-mt-3 tw-space-y-2">
              {/* View mode toggle */}
              {hasChart && (
                <div className="tw-flex tw-justify-end">
                  <ViewModeToggle
                    currentMode={viewMode}
                    onModeChange={onViewModeChange}
                    availableModes={availableModes}
                    defaultMode={defaultMode}
                  />
                </div>
              )}

              {/* Chart */}
              {showChart && activeChart && (
                <div className="tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-100 tw-p-2" style={{ maxHeight: 220 }}>
                  <DynamicChart chart={activeChart} isExpanded={isExpanded} query={queryText} />
                </div>
              )}

              {/* Table */}
              {showTable && (
                <ReportStyleTable
                  rows={result.data}
                  query={queryText}
                  reportUrl={result.reportUrl}
                  maxRows={10}
                />
              )}
            </div>
          )}
        </div>

        {/* Report link */}
        {result.reportUrl && (
          <div className="tw-mt-3 tw-pt-2 tw-border-t tw-border-gray-100">
            <a
              href={result.reportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-blue-600 hover:tw-text-blue-700 tw-transition-colors"
            >
              <BiLinkExternal size={12} />
              View Full Report
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

// Visualization Toggle Component
const ViewModeToggle: React.FC<{
  currentMode: ViewMode
  onModeChange: (mode: ViewMode) => void
  availableModes: ViewMode[]
  defaultMode: ViewMode
}> = ({ currentMode, onModeChange, availableModes, defaultMode }) => {
  const modes: { id: ViewMode; icon: React.ComponentType<any>; label: string }[] = [
    { id: 'bar', icon: BiBarChartAlt2, label: 'Bar Chart' },
    { id: 'line', icon: BiLineChart, label: 'Line Chart' },
    { id: 'pie', icon: BiPieChartAlt2, label: 'Pie Chart' },
    { id: 'table', icon: BiTable, label: 'Table' },
  ]

  const filteredModes = modes.filter(m => availableModes.includes(m.id))
  const effectiveMode = currentMode === 'auto' ? defaultMode : currentMode

  return (
    <div className="tw-flex tw-items-center tw-gap-1 tw-bg-gray-100 tw-rounded-lg tw-p-0.5">
      {filteredModes.map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          type="button"
          onClick={() => onModeChange(id)}
          title={label}
          className={`tw-p-1.5 tw-rounded-md tw-transition-all ${
            effectiveMode === id
              ? 'tw-bg-white tw-text-blue-600 tw-shadow-sm'
              : 'tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-50'
          }`}
        >
          <Icon size={14} />
        </button>
      ))}
    </div>
  )
}

// Get best default view mode based on data and query
const getDefaultViewMode = (
  chart: DetectedChart | null,
  rowCount: number,
  query: string
): ViewMode => {
  // If no chart was detected, always default to table view
  if (!chart) return 'table'

  const queryLower = query.toLowerCase()

  // Time series queries prefer line charts
  if (queryLower.includes('over time') || queryLower.includes('trend') || queryLower.includes('daily') || queryLower.includes('monthly')) {
    return 'line'
  }

  // Comparison/ranking queries prefer bar charts
  if (queryLower.includes('top') || queryLower.includes('best') || queryLower.includes('compare') || queryLower.includes('by product') || queryLower.includes('by category')) {
    return 'bar'
  }

  // Small data sets with categories work well as pie charts
  if (rowCount <= 6 && chart.type === 'pie') {
    return 'pie'
  }

  // Use detected chart type
  if (chart.type === 'line' || chart.type === 'area') return 'line'
  if (chart.type === 'bar') return 'bar'
  if (chart.type === 'pie') return 'pie'

  // Default to bar chart since we have chart data
  return 'bar'
}

// Main Dashboard Component
const AgentResultsDashboard: React.FC<AgentResultsDashboardProps> = ({
  steps,
  thoughts,
  finalResponse,
  isExpanded,
}) => {
  // State for tracking view modes per result
  const [viewModes, setViewModes] = useState<Record<number, ViewMode>>({})

  // State for dashboard customization
  const [sectionOrder, setSectionOrder] = useState<SectionType[]>(
    DEFAULT_SECTIONS.map(s => s.id)
  )
  const [sectionVisibility, setSectionVisibility] = useState<Record<SectionType, boolean>>(
    DEFAULT_SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: s.defaultVisible }), {} as Record<SectionType, boolean>)
  )
  const [customizePanelOpen, setCustomizePanelOpen] = useState(false)

  // Toggle section visibility
  const toggleSectionVisibility = (section: SectionType) => {
    setSectionVisibility(prev => ({ ...prev, [section]: !prev[section] }))
  }

  // Move section up or down
  const moveSection = (section: SectionType, direction: 'up' | 'down') => {
    setSectionOrder(prev => {
      const idx = prev.indexOf(section)
      if (idx === -1) return prev
      if (direction === 'up' && idx === 0) return prev
      if (direction === 'down' && idx === prev.length - 1) return prev

      const newOrder = [...prev]
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1
      ;[newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]]
      return newOrder
    })
  }

  // Reset to default layout
  const resetLayout = () => {
    setSectionOrder(DEFAULT_SECTIONS.map(s => s.id))
    setSectionVisibility(
      DEFAULT_SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: s.defaultVisible }), {} as Record<SectionType, boolean>)
    )
  }

  // Update view mode for a specific result
  const setViewMode = (idx: number, mode: ViewMode) => {
    setViewModes(prev => ({ ...prev, [idx]: mode }))
  }

  // Helper to check if a tool is a PayHelm MCP tool (not internal SDK tools like Read, Bash, etc.)
  const isMcpTool = (toolName: string): boolean => {
    if (!toolName) return false
    // MCP tools have prefixes like mcp__payhelm__ or payhelm__
    if (toolName.startsWith('mcp__payhelm__') || toolName.startsWith('payhelm__')) return true
    // Also allow known PayHelm tool names without prefix (for backwards compatibility with stored data)
    const knownPayhelmTools = [
      'search', 'fetch', 'get_traffic_data', 'get_traffic_metrics', 'get_traffic_summary',
      'get_ads_performance', 'get_google_ads_accounts', 'list_google_ads_client_accounts', 'get_facebook_ads_accounts', 'get_openai_ads_accounts',
      'search_google_ads', 'search_facebook_ads', 'modify_google_ads', 'modify_facebook_ads',
      'create_google_ads_pmax_campaign', 'create_google_ads_conversion', 'modify_google_ads_conversion', 'remove_google_ads_conversion',
      'get_klaviyo_accounts', 'search_klaviyo', 'get_tiktok_accounts', 'search_tiktok',
      'get_pinterest_accounts', 'search_pinterest', 'get_mailchimp_accounts', 'search_mailchimp',
      'create_mailchimp_list', 'create_mailchimp_campaign', 'update_mailchimp_campaign', 'send_mailchimp_campaign', 'replicate_mailchimp_campaign', 'delete_mailchimp_campaign',
      'create_mailchimp_member', 'add_members_to_mailchimp_list', 'tag_mailchimp_member',
      'get_mailchimp_content', 'update_mailchimp_content', 'get_mailchimp_automation_emails',
      'get_klaviyo_template', 'update_klaviyo_template', 'get_klaviyo_flow_messages',
      'get_stripe_info', 'search_stripe',
      'insure_shipment', 'void_insurance', 'update_shipping_address', 'get_current_shipping_settings',
      'export_to_email', 'export_to_csv', 'export_to_google_drive',
      'get_inventory_products', 'get_inventory_variants', 'get_inventory_summary',
      'wix_get_product', 'wix_query_products', 'wix_create_product', 'wix_edit_product', 'wix_delete_product',
      'ebay_get_inventory_item', 'ebay_get_offer', 'ebay_get_offers', 'ebay_get_listing', 'ebay_search_listings',
      'ebay_create_product', 'ebay_create_listing', 'ebay_edit_listing', 'ebay_delete_listing',
    ]
    return knownPayhelmTools.includes(toolName.toLowerCase().replace('mcp__payhelm__', ''))
  }

  // Filter completed tool steps - only show PayHelm MCP tools, not internal SDK tools (Read, Bash, Glob, etc.)
  const completedSteps = steps.filter(s => s.tool && s.status === 'completed' && isMcpTool(s.tool))

  // Aggregate all data
  const aggregated = useMemo(() => aggregateData(completedSteps, thoughts), [completedSteps, thoughts])

  // Calculate summary metrics from aggregated data
  const hasRevenue = aggregated.revenue.total > 0
  const hasOrders = aggregated.orders.total > 0
  const hasResults = aggregated.rawResults.length > 0

  // Auto-extract metrics from ALL raw results
  const allExtractedMetrics = useMemo(() => {
    const metricsMap = new Map<string, DetectedMetric>()
    for (const result of aggregated.rawResults) {
      const metrics = extractMetricsFromData(result.data, result.query)
      for (const m of metrics) {
        if (!metricsMap.has(m.label) || metricsMap.get(m.label)!.value < m.value) {
          metricsMap.set(m.label, m)
        }
      }
    }
    return Array.from(metricsMap.values()).slice(0, 6)
  }, [aggregated.rawResults])

  // If no data, show empty state
  if (completedSteps.length === 0) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-32 tw-text-gray-500">
        <FiBarChart2 size={32} className="tw-opacity-20 tw-mb-2" />
        <span className="tw-text-xs">No data available yet</span>
      </div>
    )
  }

  // Check if we have any metrics data
  const hasSpecificData = hasRevenue || hasOrders
  const hasPeriodComparisons = aggregated.periodComparisons.length > 0

  // Section render functions
  const renderReportLinks = () => {
    if (aggregated.reportUrls.length === 0) return null
    return <ReportLinksPanel urls={aggregated.reportUrls} />
  }

  const renderPeriodComparisons = () => {
    if (!hasPeriodComparisons) return null
    return (
      <div>
        <SectionHeader
          icon={BiCalendar}
          title="Period Comparison"
          subtitle={aggregated.periodComparisons.length > 1 ? `${aggregated.periodComparisons.length} reports` : undefined}
          color="indigo"
        />
        <div className="tw-space-y-4">
          {aggregated.periodComparisons.map((comp, idx) => (
            <div key={idx} className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
              {/* Report Header */}
              <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-bg-gradient-to-r tw-from-indigo-50 tw-to-blue-50 tw-border-b tw-border-gray-100">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-white tw-shadow-sm tw-flex tw-items-center tw-justify-center">
                    <FiBarChart2 className="tw-text-indigo-600" size={16} />
                  </div>
                  <div>
                    <h3 className="tw-font-semibold tw-text-gray-800 tw-text-sm">{comp.query}</h3>
                    {comp.data.periodStart && comp.data.periodEnd && (
                      <p className="tw-text-xs tw-text-gray-500">
                        {comp.data.periodStart} - {comp.data.periodEnd}
                      </p>
                    )}
                  </div>
                </div>
                {comp.reportUrl && (
                  <a
                    href={comp.reportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-blue-600 tw-text-white tw-rounded-lg tw-text-xs tw-font-medium hover:tw-bg-blue-700 tw-transition-colors tw-shadow-sm"
                  >
                    <BiLinkExternal size={12} />
                    View Full Report
                  </a>
                )}
              </div>
              {/* Chart and Table */}
              <div className="tw-p-4 tw-space-y-4">
                <PeriodComparisonChart data={comp.data} isExpanded={isExpanded} />
                <PeriodComparisonTable data={comp.data} />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const renderExecutiveSummary = () => {
    if (hasPeriodComparisons && !hasSpecificData) return null
    return (
      <div>
        <SectionHeader
          icon={FiTrendingUp}
          title="Executive Summary"
          subtitle={`${completedSteps.length} queries`}
          color="indigo"
        />
        <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-2">
          {hasRevenue && (
            <MetricCard
              icon={FiDollarSign}
              label="Total Revenue"
              value={formatCurrency(aggregated.revenue.total)}
              color="green"
            />
          )}
          {hasOrders && (
            <MetricCard
              icon={FiShoppingCart}
              label="Total Orders"
              value={formatNumber(aggregated.orders.total)}
              color="blue"
            />
          )}
          {hasResults && (
            <MetricCard
              icon={FiPackage}
              label="Data Sets"
              value={formatNumber(aggregated.rawResults.length)}
              color="amber"
            />
          )}
          {/* If no specific metrics, show auto-extracted metrics */}
          {!hasSpecificData && !hasPeriodComparisons && allExtractedMetrics.slice(0, 3).map((m, i) => (
            <MetricCard
              key={i}
              icon={m.type === 'currency' ? FiDollarSign : FiBarChart2}
              label={m.label}
              value={m.type === 'currency' ? formatCurrency(m.value) : formatNumber(m.value)}
              color={['green', 'blue', 'amber', 'purple'][i % 4]}
            />
          ))}
          <MetricCard
            icon={FiBarChart2}
            label="Queries"
            value={formatNumber(completedSteps.length)}
            color="purple"
          />
        </div>
      </div>
    )
  }

  const renderQueryResults = () => {
    if (aggregated.rawResults.length === 0) return null

    // Process all valid results with their normalized data and chart info
    // Each result is wrapped in try-catch so one bad result doesn't crash all cards
    const validResults: { result: typeof aggregated.rawResults[0]; idx: number; normalizedData: any[]; chart: DetectedChart | null }[] = []

    aggregated.rawResults.forEach((result, idx) => {
      try {
        if (result.apiError) {
          validResults.push({ result, idx, normalizedData: [], chart: null })
          return
        }

        if (!result.data || result.data.length === 0) return

        // Skip error-only results
        const firstRow = result.data[0]
        if (firstRow) {
          const keys = Object.keys(firstRow)
          if (keys.length === 1 && keys[0].toLowerCase() === 'error') return
          if (firstRow.error || firstRow.Error) {
            const nonErrorKeys = keys.filter(k => k.toLowerCase() !== 'error' && k !== '_title')
            if (nonErrorKeys.length === 0) return
          }
        }

        const normalizedData = normalizeMcpData(result.data)
        const structure = analyzeDataStructure(normalizedData, result.query)
        const chart = result.isConfigurationData ? null : detectChartType(normalizedData, structure, result.query)

        validResults.push({ result, idx, normalizedData, chart })
      } catch (err) {
        console.error(`[QueryResults] Error processing result ${idx}:`, err)
        // Still add the result with minimal processing so the card shows
        validResults.push({ result, idx, normalizedData: result.data || [], chart: null })
      }
    })

    if (validResults.length === 0) return null

    return (
      <div>
        <SectionHeader
          icon={BiBarChartAlt2}
          title="Data Sources"
          subtitle={`${validResults.length} source${validResults.length !== 1 ? 's' : ''} queried`}
          color="indigo"
        />
        {/* Responsive 2x2 grid of data source cards */}
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
          {validResults.map(({ result, idx, normalizedData, chart }) => (
            <DataSourceCard
              key={`${idx}-${result.tool || 'unknown'}-${(result.query || '').slice(0, 20)}`}
              result={result}
              index={idx}
              normalizedData={normalizedData}
              chart={chart}
              viewMode={viewModes[idx] || 'auto'}
              defaultMode={getDefaultViewMode(chart, normalizedData.length, result.query)}
              onViewModeChange={(mode) => setViewMode(idx, mode)}
              isExpanded={isExpanded}
            />
          ))}
        </div>
      </div>
    )
  }

  // Map section IDs to their render functions
  const sectionRenderers: Record<SectionType, () => React.ReactNode> = {
    'report-links': renderReportLinks,
    'period-comparisons': renderPeriodComparisons,
    'executive-summary': renderExecutiveSummary,
    'query-results': renderQueryResults,
  }

  return (
    <div className="tw-space-y-3">
      {/* Customize Button */}
      <div className="tw-flex tw-justify-end">
        <button
          type="button"
          onClick={() => setCustomizePanelOpen(!customizePanelOpen)}
          className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-medium tw-transition-all ${
            customizePanelOpen
              ? 'tw-bg-blue-100 tw-text-blue-700'
              : 'tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200'
          }`}
        >
          <BiCog size={14} />
          Customize
        </button>
      </div>

      {/* Customize Panel */}
      <DashboardCustomizePanel
        sectionOrder={sectionOrder}
        sectionVisibility={sectionVisibility}
        onToggleVisibility={toggleSectionVisibility}
        onMoveSection={moveSection}
        onReset={resetLayout}
        isOpen={customizePanelOpen}
        onClose={() => setCustomizePanelOpen(false)}
      />

      {/* Render sections in order, respecting visibility */}
      {sectionOrder.map(sectionId => {
        if (!sectionVisibility[sectionId]) return null
        try {
          const content = sectionRenderers[sectionId]()
          if (!content) return null
          return <div key={sectionId}>{content}</div>
        } catch (err) {
          console.error(`[AgentDashboard] Error rendering section ${sectionId}:`, err)
          return null
        }
      })}
    </div>
  )
}

export default AgentResultsDashboard
